import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import "../BitacoraProduccion.css";

// Material UI Icons
import LayersIcon from '@mui/icons-material/Layers';
import WineBarIcon from '@mui/icons-material/WineBar';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ScienceIcon from '@mui/icons-material/Science';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalculateIcon from '@mui/icons-material/Calculate';
import SpeedIcon from '@mui/icons-material/Speed';

// Helper robusto para extraer el Kg de Agave Crudo registrado en Cocción (materia prima inicial)
// Suma estrictamente todas las especies de maguey crudo horneadas en el lote
export const extractKgAgaveCrudo = (coccion) => {
  if (!coccion) return 0;
  if (typeof coccion === 'number' && coccion > 0) return coccion;

  // Si viene envuelto en un objeto hijo `coccion` o `bitacora_coccion`
  if (coccion.coccion) {
    const val = extractKgAgaveCrudo(coccion.coccion);
    if (val > 0) return val;
  }
  if (coccion.bitacora_coccion) {
    const val = extractKgAgaveCrudo(coccion.bitacora_coccion);
    if (val > 0) return val;
  }

  // 1. Prioridad: Si tiene arreglo de especies en cocción, sumar los kg crudos de cada especie agregada
  const magList = (Array.isArray(coccion.magueyes) && coccion.magueyes.length > 0 ? coccion.magueyes : null) ||
    (Array.isArray(coccion.coccion_maguey) && coccion.coccion_maguey.length > 0 ? coccion.coccion_maguey : null) ||
    [];
  if (magList.length > 0) {
    const sum = magList.reduce((acc, m) => {
      const v = typeof m.kgMaguey === 'number'
        ? m.kgMaguey
        : (typeof m.kg_maguey === 'number'
          ? m.kg_maguey
          : parseFloat(String(m.kgMaguey || m.kg_maguey || 0).replace(/,/g, '').replace(/[^\d.]/g, '')) || 0);
      return acc + v;
    }, 0);
    if (sum > 0) return sum;
  }

  // 2. Si viene con la propiedad explícita de kg de maguey crudo (número)
  if (typeof coccion.kgMagueyCrudo === 'number' && coccion.kgMagueyCrudo > 0) {
    return coccion.kgMagueyCrudo;
  }
  if (typeof coccion.kg_maguey_crudo === 'number' && coccion.kg_maguey_crudo > 0) {
    return coccion.kg_maguey_crudo;
  }

  // 3. Propiedades numéricas directas o en strings (snake_case, camelCase)
  const rawFields = [
    coccion.kg_maguey_crudo,
    coccion.kgMagueyCrudo,
    coccion.kg_maguey,
    coccion.kgMaguey,
    coccion.totalKgMaguey,
    coccion.total_kg_maguey,
    coccion.coccion_kg_maguey_crudo,
    coccion.kgAgave,
    coccion.kg_agave
  ];
  for (const f of rawFields) {
    if (f != null && f !== "") {
      const val = parseFloat(String(f).replace(/,/g, '').replace(/[^\d.]/g, '')) || 0;
      if (val > 0) return val;
    }
  }

  return 0;
};

// Helper para extraer el % ART de Cocción (si hay varias especies promedia el % ART de las que lo tengan)
export const extractPctArt = (coccion) => {
  if (!coccion) return 24.0;
  if (coccion.coccion) {
    const val = extractPctArt(coccion.coccion);
    if (val > 0) return val;
  }
  if (coccion.bitacora_coccion) {
    const val = extractPctArt(coccion.bitacora_coccion);
    if (val > 0) return val;
  }

  const magList = (Array.isArray(coccion.magueyes) && coccion.magueyes.length > 0 ? coccion.magueyes : null) ||
    (Array.isArray(coccion.coccion_maguey) && coccion.coccion_maguey.length > 0 ? coccion.coccion_maguey : null) ||
    [];
  const speciesWithArt = magList.filter(m => {
    const a = parseFloat(String(m.pctArt || m.pct_art || 0).replace(/[^\d.]/g, ''));
    return a > 0;
  });
  if (speciesWithArt.length > 0) {
    const sumArt = speciesWithArt.reduce((acc, m) => acc + parseFloat(String(m.pctArt || m.pct_art).replace(/[^\d.]/g, '')), 0);
    return Number((sumArt / speciesWithArt.length).toFixed(1));
  }

  const rootArt = parseFloat(String(coccion.pctArt || coccion.pct_art || coccion.pctArtCrudo || 0).replace(/[^\d.]/g, ''));
  if (rootArt > 0) return rootArt;

  return 24.0;
};

// Helper para Renderizar Badges de Estado Oficial
export const renderDestilacionStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    return <span className="badge-status aceptada">Aceptada</span>;
  }
  if (norm === 'en_revision' || norm === 'revision') {
    return <span className="badge-status revision">En Revisión</span>;
  }
  if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    return <span className="badge-status enviada">Enviada</span>;
  }
  if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    return <span className="badge-status rechazada">Rechazada</span>;
  }
  if (norm === 'cancelada' || norm === 'cancelado') {
    return <span className="badge-status cancelada">Cancelada</span>;
  }
  return <span className="badge-status borrador">Borrador</span>;
};

// Helper para Badge en Cabecera de Modal
const renderModalHeaderStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  let label = "Borrador";
  let bg = "rgba(255,255,255,0.25)";
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    label = "Aceptada por UI";
    bg = "#059669";
  } else if (norm === 'en_revision' || norm === 'revision') {
    label = "En Revisión UI";
    bg = "#D97706";
  } else if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    label = "Enviada a UI";
    bg = "#2563EB";
  } else if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    label = "Rechazada (Corrección)";
    bg = "#DC2626";
  } else if (norm === 'cancelada' || norm === 'cancelado') {
    label = "Cancelada";
    bg = "#64748B";
  }
  return (
    <span style={{ fontSize: "11.5px", padding: "3px 10px", borderRadius: "12px", background: bg, color: "#FFFFFF", fontWeight: 700, border: "1px solid rgba(255,255,255,0.4)" }}>
      {label}
    </span>
  );
};

// Banco de Ingredientes Tradicionales Categorizados para "Destilado con"
const CATEGORIZED_INGREDIENTS = [
  {
    category: "Aves & Carnes",
    icon: "🍗",
    items: ["Pechuga de Pavo", "Pechuga de Gallina Criolla", "Pechuga de Codorniz", "Conejo de Campo"]
  },
  {
    category: "Frutas Criollas & Secas",
    icon: "🍎",
    items: ["Manzana Criolla", "Plátano de Castilla", "Ciruela Pasa", "Naranja Silvestre", "Piña Criolla", "Pasas Rubias", "Tejocote", "Guayaba"]
  },
  {
    category: "Especias & Semillas",
    icon: "🌿",
    items: ["Canela en Raja", "Anís Estrella", "Almendras Tostadas", "Clavo de Olor", "Nuez Moscada", "Pimienta Gorda"]
  },
  {
    category: "Hierbas & Botánicos",
    icon: "🌱",
    items: ["Hierbabuena", "Poleo", "Toronjil", "Romero", "Hoja de Aguacate"]
  }
];

// Catálogo oficial de tipos de destiladores
export const TIPOS_DESTILADORES = [
  "Olla de barro- Montera de barro",
  "Olla de barro- Montera de madera",
  "Alambique con caldera de cobre- Montera de barro",
  "Alambique con caldera de cobre- Montera de madera",
  "Alambique con caldera de cobre- Montera de cobre",
  "Alambique con caldera de cobre- Montera de acero inoxidable",
  "Alambique",
  "Destiladores continuos",
  "Columna de cobre",
  "Columna de acero inoxidable"
];

// =========================================================================
// FUNCIÓN MATEMÁTICA DE CONTROL DE BALANCE (MOLIENDA -> 2DA DESTILACIÓN)
// =========================================================================
export const getMoliendaDestilacionBalance = (molienda, destilacionRecords = [], currentDestilacionId = null) => {
  if (!molienda) return { totalVolOrdinario: 0, volUsado: 0, volDisponible: 0, isAgotado: true, pctConsumido: 0, destilacionesCount: 0, numTinas: 0 };

  const parseVol = (val) => {
    if (val == null || val === '') return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    const cleaned = String(val).replace(/,/g, '').replace(/[^\d.]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Localizar desglose de tinas en cualquier propiedad soportada
  const tinasList = (Array.isArray(molienda.items) && molienda.items.length > 0 ? molienda.items : null) ||
    (Array.isArray(molienda.tinas) && molienda.tinas.length > 0 ? molienda.tinas : null) ||
    (Array.isArray(molienda.bitacora_produccion_molienda_detalle) && molienda.bitacora_produccion_molienda_detalle.length > 0 ? molienda.bitacora_produccion_molienda_detalle : null) ||
    (Array.isArray(molienda.tinas_desglose) && molienda.tinas_desglose.length > 0 ? molienda.tinas_desglose : null) ||
    (Array.isArray(molienda.molienda_detalle) && molienda.molienda_detalle.length > 0 ? molienda.molienda_detalle : null) ||
    (Array.isArray(molienda.detalles) && molienda.detalles.length > 0 ? molienda.detalles : null) ||
    (molienda.molienda && Array.isArray(molienda.molienda.items) ? molienda.molienda.items : null) ||
    [];

  // Sumar volumen total obtenido en 1ra destilación sumando todas las tinas agregadas
  let totalVolOrdinario = 0;
  if (tinasList.length > 0) {
    totalVolOrdinario = tinasList.reduce((acc, it) => {
      // 1. Si la tina ya tiene el cálculo consolidado
      if (typeof it.totalTina1raDestilacion === 'number' && it.totalTina1raDestilacion > 0) {
        return acc + it.totalTina1raDestilacion;
      }

      // 2. Volumen Ordinario (a rectificar)
      const ordVol = Math.max(
        parseVol(it.rawVolumenOrdinario),
        parseVol(it.rawVolumenMezcal),
        parseVol(it.volumenOrdinario),
        parseVol(it.volumen_ordinario),
        parseVol(it.volumenMezcal),
        parseVol(it.volumen_mezcal),
        parseVol(it.volOrd),
        parseVol(it.vol_ord)
      );

      // 3. Volumen Mezcal / Colas de 1ra destilación
      const colVol = Math.max(
        parseVol(it.rawVolumenColas),
        parseVol(it.volumenColas),
        parseVol(it.volumen_colas),
        parseVol(it.volCol),
        parseVol(it.vol_col)
      );

      const tinaTotal = (ordVol + colVol) > 0 ? (ordVol + colVol) : parseVol(it.volumen || it.volumen_1ra || it.totalVol);
      return acc + tinaTotal;
    }, 0);
  }

  // Si no se encontraron tinas con volumen numérico, usar totales directos a nivel de molienda
  if (totalVolOrdinario <= 0) {
    const rootOrd = Math.max(
      parseVol(molienda.totalVolOrdinario),
      parseVol(molienda.total_vol_ordinario),
      parseVol(molienda.total_volumen_ordinario),
      parseVol(molienda.totalVolMezcal),
      parseVol(molienda.total_vol_mezcal),
      parseVol(molienda.total_volumen_mezcal),
      parseVol(molienda.volumenMezcal),
      parseVol(molienda.volumenOrdinario)
    );
    const rootCol = Math.max(
      parseVol(molienda.totalVolColas),
      parseVol(molienda.total_vol_colas),
      parseVol(molienda.total_volumen_colas),
      parseVol(molienda.volumenColas)
    );
    totalVolOrdinario = (rootOrd + rootCol) > 0 ? (rootOrd + rootCol) : parseVol(molienda.totalVolOrdinario || molienda.totalVolMezcal || molienda.volumenMezcal);
  }

  const noProd = String(molienda.noProduccion || molienda.no_produccion || '').trim();
  const moliendaId = String(molienda.rawId || molienda.id || molienda.id_bitacora_molienda || '').trim();

  // Filtrar destilaciones que usaron este lote de molienda
  const matchingDestilaciones = (Array.isArray(destilacionRecords) ? destilacionRecords : []).filter(d => {
    if (currentDestilacionId && String(d.rawId || d.id || d.id_bitacora_destilacion) === String(currentDestilacionId)) {
      return false;
    }
    const isCancelled = String(d.status || d.estatus || '').toLowerCase().includes('cancelad');
    if (isCancelled) return false;

    const dNoProd = String(d.noProduccion || d.no_produccion || '').trim();
    const dMoliendaId = String(d.id_bitacora_molienda || d.idBitacoraMolienda || '').trim();

    return (noProd && dNoProd === noProd) || (moliendaId && dMoliendaId === moliendaId);
  });

  // Sumar volumen de 1ra cargado en las destilaciones
  const volUsado = matchingDestilaciones.reduce((acc, d) => {
    const items = Array.isArray(d.items) && d.items.length > 0 ? d.items : (
      Array.isArray(d.alambiques) && d.alambiques.length > 0 ? d.alambiques : (
        Array.isArray(d.bitacora_produccion_destilacion_detalle) ? d.bitacora_produccion_destilacion_detalle : []
      )
    );
    const destVol = items.reduce((tAcc, it) => tAcc + parseVol(it.volADestilar || it.vol_a_destilar || it.volumen_a_destilar || it.volumenADestilar), 0);
    const fallbackVol = parseVol(d.totalVolCargado || d.volADestilar || d.volumen_a_destilar);
    return acc + (destVol > 0 ? destVol : fallbackVol);
  }, 0);

  const volDisponible = Math.max(0, totalVolOrdinario - volUsado);
  const isAgotado = totalVolOrdinario > 0 ? (volDisponible <= 0) : false;
  const pctConsumido = totalVolOrdinario > 0 ? Math.min(100, Math.round((volUsado / totalVolOrdinario) * 100)) : 0;

  return {
    totalVolOrdinario,
    volUsado,
    volDisponible,
    isAgotado,
    pctConsumido,
    destilacionesCount: matchingDestilaciones.length,
    numTinas: tinasList.length
  };
};

// =========================================================================
// FUNCIÓN PARA EXTRAER DESGLOSE DE 1RA DESTILACIÓN (VOL. MEZCAL & VOL. ORDINARIO)
// =========================================================================
export const extract1raDestilacionVolumes = (molienda) => {
  if (!molienda) {
    return {
      volMezcal: 0,
      pctAlcMezcal: "18.0",
      volOrdinario: 0,
      pctAlcOrdinario: "26.5"
    };
  }

  const parseVol = (val) => {
    if (val == null || val === '') return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    const cleaned = String(val).replace(/,/g, '').replace(/[^\d.]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const tinasList = (Array.isArray(molienda.items) && molienda.items.length > 0 ? molienda.items : null) ||
    (Array.isArray(molienda.tinas) && molienda.tinas.length > 0 ? molienda.tinas : null) ||
    (Array.isArray(molienda.bitacora_produccion_molienda_detalle) && molienda.bitacora_produccion_molienda_detalle.length > 0 ? molienda.bitacora_produccion_molienda_detalle : null) ||
    (Array.isArray(molienda.tinas_desglose) && molienda.tinas_desglose.length > 0 ? molienda.tinas_desglose : null) ||
    (Array.isArray(molienda.molienda_detalle) && molienda.molienda_detalle.length > 0 ? molienda.molienda_detalle : null) ||
    (Array.isArray(molienda.detalles) && molienda.detalles.length > 0 ? molienda.detalles : null) ||
    (molienda.molienda && Array.isArray(molienda.molienda.items) ? molienda.molienda.items : null) ||
    [];

  let sumVolMezcal = 0;
  let sumPureMezcal = 0;
  let sumVolOrdinario = 0;
  let sumPureOrdinario = 0;
  let lastPctMezcal = null;
  let lastPctOrdinario = null;

  if (tinasList.length > 0) {
    tinasList.forEach(it => {
      // 1. VOL. MEZCAL (campo volumenColas / rawVolumenColas / volumenMezcal1ra)
      const vMez = Math.max(
        parseVol(it.rawVolumenColas),
        parseVol(it.volumenColas),
        parseVol(it.volumen_colas),
        parseVol(it.volCol),
        parseVol(it.vol_col),
        parseVol(it.volumenMezcal1ra),
        parseVol(it.vol_mezcal_1ra)
      );
      const aMez = parseVol(it.pctAlcColas || it.pct_alc_colas || it.pctAlcMezcal1ra || it.pct_alc_mezcal_1ra);
      if (aMez > 0) {
        lastPctMezcal = aMez;
        sumPureMezcal += (vMez * aMez);
      }
      sumVolMezcal += vMez;

      // 2. VOL. ORDINARIO (A RECTIFICAR)
      const vOrd = Math.max(
        parseVol(it.rawVolumenOrdinario),
        parseVol(it.rawVolumenMezcal),
        parseVol(it.volumenOrdinario),
        parseVol(it.volumen_ordinario),
        parseVol(it.volumenMezcal),
        parseVol(it.volumen_mezcal),
        parseVol(it.volOrd),
        parseVol(it.vol_ord)
      );
      const aOrd = parseVol(it.pctAlcOrdinario || it.pct_alc_ordinario || it.pctAlcMezcal || it.pct_alc_mezcal);
      if (aOrd > 0) {
        lastPctOrdinario = aOrd;
        sumPureOrdinario += (vOrd * aOrd);
      }
      sumVolOrdinario += vOrd;
    });
  }

  // Fallbacks si tinas no tenían desglosado a nivel item
  if (sumVolMezcal <= 0) {
    sumVolMezcal = Math.max(
      parseVol(molienda.totalVolColas),
      parseVol(molienda.total_vol_colas),
      parseVol(molienda.volumenColas),
      parseVol(molienda.volumen_colas),
      parseVol(molienda.volumenMezcal1ra)
    );
  }

  if (sumVolOrdinario <= 0) {
    sumVolOrdinario = Math.max(
      parseVol(molienda.totalVolOrdinario),
      parseVol(molienda.total_vol_ordinario),
      parseVol(molienda.volumenOrdinario),
      parseVol(molienda.volumen_ordinario)
    );
    if (sumVolOrdinario <= 0 && parseVol(molienda.volumenMezcal) > 0 && parseVol(molienda.volumenMezcal) !== sumVolMezcal) {
      sumVolOrdinario = parseVol(molienda.volumenMezcal);
    }
  }

  const avgPctMezcal = sumVolMezcal > 0 && sumPureMezcal > 0
    ? (sumPureMezcal / sumVolMezcal).toFixed(1)
    : (lastPctMezcal ? String(lastPctMezcal) : "18.0");

  const avgPctOrdinario = sumVolOrdinario > 0 && sumPureOrdinario > 0
    ? (sumPureOrdinario / sumVolOrdinario).toFixed(1)
    : (lastPctOrdinario ? String(lastPctOrdinario) : "26.5");

  return {
    volMezcal: sumVolMezcal,
    pctAlcMezcal: avgPctMezcal,
    volOrdinario: sumVolOrdinario,
    pctAlcOrdinario: avgPctOrdinario
  };
};

// =========================================================================
// CREAR AUTOMÁTICAMENTE LOS 2 REGISTROS DE DESTILACIÓN (MEZCAL Y ORDINARIO)
// =========================================================================
export const createDestilacionItemsFromMolienda = (molienda, baseDate = null) => {
  const today = baseDate || new Date().toISOString().split('T')[0];

  if (!molienda) {
    return [
      {
        id: Date.now(),
        fechaSegunda: today,
        destiladorNum: "Alambique 01 - Mezcal (1ra Dest.)",
        tipoCarga: "mezcal",
        tipoAlambique: "",
        nuevoLote: "",
        volADestilar: "",
        esDestiladoCon: false,
        destiladoConIngrediente: "",
        destiladoConIngredientes: [],
        puntasVol: "",
        puntasAlc: "65.0",
        mezcalVol: "",
        mezcalAlc: "48.5",
        colasVol: "",
        colasAlc: "20.0"
      },
      {
        id: Date.now() + 1,
        fechaSegunda: today,
        destiladorNum: "Alambique 02 - Ordinario (1ra Dest.)",
        tipoCarga: "ordinario",
        tipoAlambique: "",
        nuevoLote: "",
        volADestilar: "",
        esDestiladoCon: false,
        destiladoConIngrediente: "",
        destiladoConIngredientes: [],
        puntasVol: "",
        puntasAlc: "65.0",
        mezcalVol: "",
        mezcalAlc: "48.5",
        colasVol: "",
        colasAlc: "20.0"
      }
    ];
  }

  const { volMezcal, pctAlcMezcal, volOrdinario, pctAlcOrdinario } = extract1raDestilacionVolumes(molienda);

  // Registro 1: VOL. MEZCAL obtenido en 1ra destilación
  const vMezNum = volMezcal > 0 ? volMezcal : 0;
  const itemMezcal = {
    id: Date.now(),
    fechaSegunda: today,
    destiladorNum: "Alambique 01 - Mezcal (1ra Dest.)",
    tipoCarga: "mezcal",
    tipoAlambique: "",
    nuevoLote: "",
    volADestilar: vMezNum > 0 ? String(vMezNum) : "",
    esDestiladoCon: false,
    destiladoConIngrediente: "",
    destiladoConIngredientes: [],
    puntasVol: vMezNum > 0 ? String(Math.round(vMezNum * 0.05)) : "",
    puntasAlc: "65.0",
    mezcalVol: vMezNum > 0 ? String(Math.round(vMezNum * 0.45)) : "",
    mezcalAlc: "48.5",
    colasVol: vMezNum > 0 ? String(Math.round(vMezNum * 0.10)) : "",
    colasAlc: "20.0"
  };

  // Registro 2: VOL. ORDINARIO (A RECTIFICAR)
  const vOrdNum = volOrdinario > 0 ? volOrdinario : 0;
  const itemOrdinario = {
    id: Date.now() + 1,
    fechaSegunda: today,
    destiladorNum: "Alambique 02 - Ordinario (1ra Dest.)",
    tipoCarga: "ordinario",
    tipoAlambique: "",
    nuevoLote: "",
    volADestilar: vOrdNum > 0 ? String(vOrdNum) : "",
    esDestiladoCon: false,
    destiladoConIngrediente: "",
    destiladoConIngredientes: [],
    puntasVol: vOrdNum > 0 ? String(Math.round(vOrdNum * 0.05)) : "",
    puntasAlc: "65.0",
    mezcalVol: vOrdNum > 0 ? String(Math.round(vOrdNum * 0.45)) : "",
    mezcalAlc: "48.5",
    colasVol: vOrdNum > 0 ? String(Math.round(vOrdNum * 0.10)) : "",
    colasAlc: "20.0"
  };

  return [itemMezcal, itemOrdinario];
};

const DestilacionSection = ({
  destilacionRecords = [],
  moliendaRecords = [],
  coccionRecords = [],
  pagination,
  destilacionSearch: propSearch,
  onSearchChange: propOnSearchChange,
  onPageChange,
  statusFilter: propStatusFilter,
  onStatusFilterChange,
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveDestilacion,
  handleCancelDestilacion,
  handleRejectDestilacion,
  onOpenNewDestilacion,
  onOpenNewGranel
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [localSearch, setLocalSearch] = useState("");
  const [localStatusFilter, setLocalStatusFilter] = useState("todos");
  const [localClaseFilter, setLocalClaseFilter] = useState("todos");
  const [activeCategoryTab, setActiveCategoryTab] = useState(0);

  const search = propSearch !== undefined ? propSearch : localSearch;
  const statusFilter = propStatusFilter !== undefined ? propStatusFilter : localStatusFilter;

  const handleSearchChange = (val) => {
    setLocalSearch(val);
    if (propOnSearchChange) propOnSearchChange(val);
  };

  const handleStatusFilterChange = (val) => {
    setLocalStatusFilter(val);
    if (onStatusFilterChange) onStatusFilterChange(val);
  };

  // Moliendas disponibles para destilación (permite cualquier estatus: borrador, enviada, aceptada, etc.)
  const availableMoliendas = useMemo(() => {
    return (Array.isArray(moliendaRecords) ? moliendaRecords : []).filter(m => {
      const st = String(m.status || m.statusUI || '').toLowerCase();
      return !st.includes('cancelad');
    });
  }, [moliendaRecords]);

  // Molienda base seleccionada en el formulario (ÚNICAMENTE SI SE HA SELECCIONADO UNA)
  const formMolienda = useMemo(() => {
    if (!formData?.noProduccion && !formData?.id_bitacora_molienda) {
      return null;
    }
    const match = moliendaRecords.find(m =>
      (formData.noProduccion && (m.noProduccion === formData.noProduccion || m.id === formData.noProduccion)) ||
      (formData.id_bitacora_molienda && (String(m.rawId) === String(formData.id_bitacora_molienda) || String(m.idBitacoraMolienda) === String(formData.id_bitacora_molienda) || String(m.id) === String(formData.id_bitacora_molienda)))
    );
    return match || null;
  }, [formData?.noProduccion, formData?.id_bitacora_molienda, moliendaRecords]);

  // Saldo de Mezcal de 1ra (Ordinario) de la Molienda seleccionada
  const moliendaBalance = useMemo(() => {
    if (!formMolienda) return { totalVolOrdinario: 0, volUsado: 0, volDisponible: 0, isAgotado: false, pctConsumido: 0 };
    return getMoliendaDestilacionBalance(formMolienda, destilacionRecords, formData.rawId || formData.id);
  }, [formMolienda, destilacionRecords, formData.rawId, formData.id]);

  const { totalVolOrdinario, volUsado, volDisponible, isAgotado: isMoliendaAgotada, pctConsumido } = moliendaBalance;

  // Items garantizados de destilación (2 registros automáticos: Mezcal y Ordinario)
  const destilacionItems = useMemo(() => {
    if (Array.isArray(formData?.items) && formData.items.length > 0) {
      return formData.items;
    }
    const today = new Date().toISOString().split('T')[0];

    if (formMolienda) {
      return createDestilacionItemsFromMolienda(formMolienda, today);
    }

    return createDestilacionItemsFromMolienda(null, today);
  }, [formData?.items, formMolienda]);

  // Totales calculados en tiempo real para el formulario
  const formTotalVolADestilar = destilacionItems.reduce((acc, it) => acc + (parseFloat(String(it.volADestilar).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalMezcal = destilacionItems.reduce((acc, it) => acc + (parseFloat(String(it.mezcalVol).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalPuntas = destilacionItems.reduce((acc, it) => acc + (parseFloat(String(it.puntasVol).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalColas = destilacionItems.reduce((acc, it) => acc + (parseFloat(String(it.colasVol).replace(/[^\d.]/g, '')) || 0), 0);

  // Grado alcohólico ponderado en vivo del Mezcal Refinado
  const formTotalPureAlc = destilacionItems.reduce((acc, it) => {
    const vol = parseFloat(String(it.mezcalVol).replace(/[^\d.]/g, '')) || 0;
    const alc = parseFloat(String(it.mezcalAlc).replace(/[^\d.]/g, '')) || 0;
    return acc + (vol * alc);
  }, 0);
  const formAvgAlcMezcal = formTotalMezcal > 0 ? (formTotalPureAlc / formTotalMezcal).toFixed(1) : "0.0";

  // Rendimiento de Rectificación en vivo (%)
  const formRendimientoPct = formTotalVolADestilar > 0 ? Math.min(100, Math.round((formTotalMezcal / formTotalVolADestilar) * 100)) : 0;

  // Estado del saldo remanente
  const saldoRemanente = volDisponible - formTotalVolADestilar;
  const isExcesoVol = formMolienda && formTotalVolADestilar > volDisponible && volDisponible > 0;

  // =========================================================================
  // FÓRMULAS DE RENDIMIENTOS Y EFICIENCIA DE DESTILACIÓN (NOM-070-SCFI-2016)
  // CÁLCULO GENERAL CONSOLIDADO DE TODOS LOS ALAMBIQUES DEL LOTE
  // =========================================================================
  // Relación con Cocción para detectar Kg de Maguey Crudo y % ART
  const formCoccion = useMemo(() => {
    const list = Array.isArray(coccionRecords) ? coccionRecords : [];
    const currentNoProd = String(formData?.noProduccion || '').trim().toLowerCase();

    // 1. Si la molienda ya tiene el objeto cocción asociado directamente
    if (formMolienda?.coccion) {
      return formMolienda.coccion;
    }

    // 2. Coincidencia a través de la molienda seleccionada
    if (formMolienda) {
      const mCoccionId = String(formMolienda.id_bitacora_coccion || formMolienda.idBitacoraCoccion || formMolienda.id_coccion || '').trim();
      if (mCoccionId) {
        const match = list.find(c => {
          const cId = String(c.rawId || c.id || c.idBitacoraCoccion || c.id_bitacora_coccion || '').trim();
          return cId && cId === mCoccionId;
        });
        if (match) return match;
      }

      const mNoProd = String(formMolienda.noProduccion || formMolienda.no_produccion || '').trim().toLowerCase();
      if (mNoProd) {
        const match = list.find(c => {
          const cNoProd = String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase();
          return cNoProd && (cNoProd === mNoProd || cNoProd.includes(mNoProd) || mNoProd.includes(cNoProd));
        });
        if (match) return match;
      }
    }

    // 3. Coincidencia directa por noProduccion con cocción
    if (currentNoProd) {
      const match = list.find(c => {
        const cNoProd = String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase();
        return cNoProd && (cNoProd === currentNoProd || cNoProd.includes(currentNoProd) || currentNoProd.includes(cNoProd));
      });
      if (match) return match;
    }

    return null;
  }, [formData?.noProduccion, formMolienda, coccionRecords]);

  // Detección estricta de Kg de Agave Crudo registrado en Cocción (materia prima inicial)
  const detectedKgAgaveCrudo = useMemo(() => {
    return extractKgAgaveCrudo(formCoccion);
  }, [formCoccion]);

  const detectedPctArt = useMemo(() => {
    return extractPctArt(formCoccion);
  }, [formCoccion]);

  // Sincronización automática de Kg Agave Crudo de Cocción al seleccionar el lote de Molienda
  useEffect(() => {
    if (detectedKgAgaveCrudo > 0) {
      setFormData(prev => {
        if (!prev.kgAgave || Number(prev.kgAgave) === 0 || prev.kgAgave !== String(detectedKgAgaveCrudo)) {
          return {
            ...prev,
            kgAgave: String(detectedKgAgaveCrudo)
          };
        }
        return prev;
      });
    }
  }, [detectedKgAgaveCrudo, setFormData]);

  // Valor reactivo de Kg Agave Crudo para las fórmulas (prioriza el valor detectado automáticamente si no hay uno manual mayor a 0)
  const formKgAgave = (formData.kgAgave !== undefined && formData.kgAgave !== null && formData.kgAgave !== "" && Number(formData.kgAgave) > 0)
    ? (parseFloat(String(formData.kgAgave).replace(/,/g, '').replace(/[^\d.]/g, '')) || 0)
    : (detectedKgAgaveCrudo > 0 ? detectedKgAgaveCrudo : 0);

  const formPctArt = (formData.pctArt !== undefined && formData.pctArt !== null && formData.pctArt !== "" && Number(formData.pctArt) > 0)
    ? (parseFloat(String(formData.pctArt).replace(/[^\d.]/g, '')) || 0)
    : (detectedPctArt > 0 ? detectedPctArt : 24.0);

  // -------------------------------------------------------------------------
  // SUMATORIA GENERAL DE TODOS LOS ALAMBIQUES AGREGADOS:
  // Se suman todas las puntas, luego todo el mezcal y luego todas las colas
  // -------------------------------------------------------------------------
  // 1. PUNTAS CONSOLIDADAS:
  const formTotalPuntasVol = formTotalPuntas;
  const formTotalPuntasPureAlc = destilacionItems.reduce((acc, it) => {
    const v = parseFloat(String(it.puntasVol || 0).replace(/[^\d.]/g, '')) || 0;
    const a = parseFloat(String(it.puntasAlc != null && it.puntasAlc !== '' ? it.puntasAlc : '65.0').replace(/[^\d.]/g, '')) || 65.0;
    return acc + (v * a);
  }, 0);
  const formAvgAlcPuntas = formTotalPuntasVol > 0 ? (formTotalPuntasPureAlc / formTotalPuntasVol).toFixed(1) : "0.0";
  // Puntas a 45%: Sumatoria de todas las puntas convertidas a 45% Alc. Vol.
  const formPuntas45 = formTotalPuntasPureAlc / 45;

  // 2. MEZCAL REFINADO CONSOLIDADO:
  const formTotalMezcalVol = formTotalMezcal;
  const formTotalMezcalPureAlc = destilacionItems.reduce((acc, it) => {
    const v = parseFloat(String(it.mezcalVol || 0).replace(/[^\d.]/g, '')) || 0;
    const a = parseFloat(String(it.mezcalAlc != null && it.mezcalAlc !== '' ? it.mezcalAlc : '48.5').replace(/[^\d.]/g, '')) || 48.5;
    return acc + (v * a);
  }, 0);
  // Mezcal a 45%: Sumatoria de todo el mezcal convertido a 45% Alc. Vol.
  const formMezcal45 = formTotalMezcalPureAlc / 45;

  // 3. COLAS CONSOLIDADAS:
  const formTotalColasVol = formTotalColas;
  const formTotalColasPureAlc = destilacionItems.reduce((acc, it) => {
    const v = parseFloat(String(it.colasVol || 0).replace(/[^\d.]/g, '')) || 0;
    const a = parseFloat(String(it.colasAlc != null && it.colasAlc !== '' ? it.colasAlc : '20.0').replace(/[^\d.]/g, '')) || 20.0;
    return acc + (v * a);
  }, 0);
  const formAvgAlcColas = formTotalColasVol > 0 ? (formTotalColasPureAlc / formTotalColasVol).toFixed(1) : "0.0";
  // Colas a 45%: Sumatoria de todas las colas convertidas a 45% Alc. Vol.
  const formColas45 = formTotalColasPureAlc / 45;

  // DENOMINADOR GENERAL R1 = (Mezcal 45%) + (Puntas 45%) + (Colas 45%)
  const formTotalCortes45 = formMezcal45 + formPuntas45 + formColas45;

  // 4. FÓRMULA R1 - RENDIMIENTO REAL GENERAL:
  // R1 = Kg Agave Crudo / [ (Mezcal 45%) + (Puntas 45%) + (Colas 45%) ]
  const formR1 = (formTotalCortes45 > 0 && formKgAgave > 0)
    ? (formKgAgave / formTotalCortes45)
    : 0;

  // 5. FÓRMULA R2 - RENDIMIENTO TEÓRICO GENERAL:
  // R2 = Kg de Agave Crudo / [ ((Kg de Agave Crudo) * (%ART)/100) * 1.4404 ]
  const formLitrosTeoricos45 = formKgAgave > 0 && formPctArt > 0
    ? (formKgAgave * (formPctArt / 100)) * 1.4404
    : 0;

  const formR2 = formLitrosTeoricos45 > 0 && formKgAgave > 0
    ? (formKgAgave / formLitrosTeoricos45)
    : (formPctArt > 0 ? (100 / (formPctArt * 1.4404)) : 0);

  // 6. FÓRMULA E - % EFICIENCIA GENERAL:
  // E = (RENDIMIENTO TEÓRICO / RENDIMIENTO REAL) * 100
  const formEficiencia = (formR1 > 0 && formR2 > 0)
    ? ((formR2 / formR1) * 100)
    : 0;

  // Objeto de submission garantizado
  const formSubmissionData = {
    ...formData,
    kgAgave: formKgAgave > 0 ? formKgAgave : undefined,
    pctArt: formPctArt > 0 ? formPctArt : undefined,
    rendimientoReal: formR1 > 0 ? Number(formR1.toFixed(2)) : undefined,
    rendimientoTeorico: formR2 > 0 ? Number(formR2.toFixed(2)) : undefined,
    eficienciaPct: formEficiencia > 0 ? Number(formEficiencia.toFixed(1)) : undefined,
    items: destilacionItems
  };

  // Cálculo consolidado para el registro seleccionado en Modal 1 (Detalle Completo)
  const selectedRecordCalc = useMemo(() => {
    if (!selectedRecord) return null;
    const items = Array.isArray(selectedRecord.items) ? selectedRecord.items : [];

    const matchedMolienda = (Array.isArray(moliendaRecords) ? moliendaRecords : []).find(m =>
      (selectedRecord.noProduccion && (m.noProduccion === selectedRecord.noProduccion || m.id === selectedRecord.noProduccion)) ||
      (selectedRecord.id_bitacora_molienda && (String(m.rawId) === String(selectedRecord.id_bitacora_molienda) || String(m.id) === String(selectedRecord.id_bitacora_molienda)))
    );

    const matchedCoccion = (Array.isArray(coccionRecords) ? coccionRecords : []).find(c =>
      (selectedRecord.noProduccion && String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase() === String(selectedRecord.noProduccion).trim().toLowerCase()) ||
      (matchedMolienda?.id_bitacora_coccion && (String(c.rawId) === String(matchedMolienda.id_bitacora_coccion) || String(c.idBitacoraCoccion) === String(matchedMolienda.id_bitacora_coccion) || String(c.id) === String(matchedMolienda.id_bitacora_coccion))) ||
      (matchedMolienda?.noProduccion && String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase() === String(matchedMolienda.noProduccion).trim().toLowerCase())
    ) || null;

    const detectedCrudo = extractKgAgaveCrudo(matchedCoccion);
    const kgAgave = parseFloat(String(selectedRecord.kgAgave || selectedRecord.kg_agave || detectedCrudo || 0).replace(/,/g, '').replace(/[^\d.]/g, '')) || 0;
    const pctArt = parseFloat(String(selectedRecord.pctArt || selectedRecord.pct_art || matchedCoccion?.pctArt || matchedCoccion?.pct_art || 24.0).replace(/[^\d.]/g, '')) || 24.0;

    const mezcalVol = items.reduce((acc, it) => acc + (parseFloat(String(it.mezcalVol || it.volumenMezcal || (it.mezcal ? String(it.mezcal).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0), 0);
    const mezcalPureAlc = items.reduce((acc, it) => {
      const v = parseFloat(String(it.mezcalVol || it.volumenMezcal || (it.mezcal ? String(it.mezcal).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0;
      const a = parseFloat(String(it.mezcalAlc || it.pctAlcMezcal || 0).replace(/[^\d.]/g, '')) || 0;
      return acc + (v * a);
    }, 0);
    const mezcal45 = mezcalPureAlc / 45;

    const puntasVol = items.reduce((acc, it) => acc + (parseFloat(String(it.puntasVol || it.puntas_volumen || (it.puntas ? String(it.puntas).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0), 0);
    const puntasPureAlc = items.reduce((acc, it) => {
      const v = parseFloat(String(it.puntasVol || it.puntas_volumen || (it.puntas ? String(it.puntas).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0;
      const a = parseFloat(String(it.puntasAlc || it.puntas_pct_alc || 0).replace(/[^\d.]/g, '')) || 0;
      return acc + (v * a);
    }, 0);
    const puntas45 = puntasPureAlc / 45;

    const colasVol = items.reduce((acc, it) => acc + (parseFloat(String(it.colasVol || it.colas_volumen || (it.colas ? String(it.colas).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0), 0);
    const colasPureAlc = items.reduce((acc, it) => {
      const v = parseFloat(String(it.colasVol || it.colas_volumen || (it.colas ? String(it.colas).split(' ')[0] : 0)).replace(/[^\d.]/g, '')) || 0;
      const a = parseFloat(String(it.colasAlc || it.colas_pct_alc || 0).replace(/[^\d.]/g, '')) || 0;
      return acc + (v * a);
    }, 0);
    const colas45 = colasPureAlc / 45;

    const total45 = mezcal45 + puntas45 + colas45;
    const r1 = (total45 > 0 && kgAgave > 0) ? (kgAgave / total45) : (parseFloat(selectedRecord.rendimientoReal || selectedRecord.rendimiento_real || 0) || 0);

    const litrosTeoricos45 = kgAgave > 0 && pctArt > 0 ? (kgAgave * (pctArt / 100)) * 1.4404 : 0;
    const r2 = litrosTeoricos45 > 0 && kgAgave > 0 ? (kgAgave / litrosTeoricos45) : (pctArt > 0 ? (100 / (pctArt * 1.4404)) : 0);

    const eficiencia = (r1 > 0 && r2 > 0) ? ((r2 / r1) * 100) : (parseFloat(selectedRecord.eficienciaPct || selectedRecord.eficiencia_pct || 0) || 0);

    return {
      kgAgave,
      pctArt,
      mezcalVol,
      mezcal45,
      puntasVol,
      puntas45,
      colasVol,
      colas45,
      total45,
      litrosTeoricos45,
      r1: Number(r1),
      r2: Number(r2),
      eficiencia: Number(eficiencia),
      alambiquesCount: items.length
    };
  }, [selectedRecord, moliendaRecords, coccionRecords]);

  // =========================================================================
  // CÁLCULO DE KPIS CONSOLIDADOS PARA LA SECCIÓN SUPERIOR
  // =========================================================================
  const kpiStats = useMemo(() => {
    const totalRecords = destilacionRecords.length;
    let totalOrdinarioCargado = 0;
    let totalMezcalObtenido = 0;
    let totalPureAlc = 0;
    let totalDestiladoCon = 0;

    destilacionRecords.forEach(rec => {
      const isCancelled = String(rec.status || rec.statusUI || '').toLowerCase().includes('cancelad');
      if (isCancelled) return;

      if (Array.isArray(rec.items) && rec.items.length > 0) {
        rec.items.forEach(it => {
          const volCarga = parseFloat(String(it.volADestilar || it.vol_a_destilar || 0).replace(/[^\d.]/g, '')) || 0;
          const volMez = parseFloat(String(it.mezcalVol || it.volumenMezcal || 0).replace(/[^\d.]/g, '')) || 0;
          const alcMez = parseFloat(String(it.mezcalAlc || it.pctAlcMezcal || 0).replace(/[^\d.]/g, '')) || 0;
          totalOrdinarioCargado += volCarga;
          totalMezcalObtenido += volMez;
          totalPureAlc += (volMez * alcMez);
          if (it.esDestiladoCon || (it.destiladoConIngredientes && it.destiladoConIngredientes.length > 0)) {
            totalDestiladoCon++;
          }
        });
      } else {
        const volCarga = parseFloat(String(rec.volADestilar || rec.totalVolCargado || 0).replace(/[^\d.]/g, '')) || 0;
        const volMez = parseFloat(String(rec.mezcal || rec.totalMezcalObtenido || 0).replace(/[^\d.]/g, '')) || 0;
        const alcMez = parseFloat(String(rec.avgAlcMezcal || 48.0).replace(/[^\d.]/g, '')) || 0;
        totalOrdinarioCargado += volCarga;
        totalMezcalObtenido += volMez;
        totalPureAlc += (volMez * alcMez);
      }
    });

    const avgAlcGlobal = totalMezcalObtenido > 0 ? (totalPureAlc / totalMezcalObtenido).toFixed(1) : "0.0";
    const rendimientoGlobal = totalOrdinarioCargado > 0 ? Math.round((totalMezcalObtenido / totalOrdinarioCargado) * 100) : 0;

    return {
      totalRecords,
      totalOrdinarioCargado,
      totalMezcalObtenido,
      avgAlcGlobal,
      rendimientoGlobal,
      totalDestiladoCon
    };
  }, [destilacionRecords]);

  // =========================================================================
  // FILTRADO DE REGISTROS DE LA TABLA HISTÓRICA
  // =========================================================================
  const filteredRecords = useMemo(() => {
    return destilacionRecords.filter(rec => {
      const normStatus = String(rec.status || rec.statusUI || "borrador").toLowerCase().trim();
      if (statusFilter !== "todos") {
        if (statusFilter === "aceptada") {
          if (!normStatus.includes("aceptad") && !normStatus.includes("aprobad")) return false;
        } else if (statusFilter === "enviada") {
          if (!normStatus.includes("enviad") && !normStatus.includes("revision") && !normStatus.includes("pendiente")) return false;
        } else if (statusFilter === "rechazada") {
          if (!normStatus.includes("rechazad")) return false;
        } else if (statusFilter === "cancelada") {
          if (!normStatus.includes("cancelad")) return false;
        } else if (statusFilter === "borrador") {
          if (!normStatus.includes("borrador")) return false;
        }
      }

      // Filtro por clasificación
      if (localClaseFilter !== "todos") {
        const hasDestiladoCon = rec.items && Array.isArray(rec.items)
          ? rec.items.some(it => it.esDestiladoCon || (it.destiladoConIngredientes && it.destiladoConIngredientes.length > 0))
          : Boolean(rec.esDestiladoCon);

        if (localClaseFilter === "destilado_con" && !hasDestiladoCon) return false;
        if (localClaseFilter === "joven" && hasDestiladoCon) return false;
      }

      if (search) {
        const term = search.toLowerCase();
        const noProdMatch = rec.noProduccion && rec.noProduccion.toLowerCase().includes(term);
        const inspMatch = rec.inspector && rec.inspector.toLowerCase().includes(term);
        const obsMatch = rec.observaciones && rec.observaciones.toLowerCase().includes(term);
        const itemMatch = rec.items && rec.items.some(it =>
          (it.destiladorNum && String(it.destiladorNum).toLowerCase().includes(term)) ||
          (it.tipoAlambique && String(it.tipoAlambique).toLowerCase().includes(term)) ||
          (it.fechaSegunda && String(it.fechaSegunda).toLowerCase().includes(term)) ||
          (it.nuevoLote && String(it.nuevoLote).toLowerCase().includes(term)) ||
          (it.destiladoConIngrediente && String(it.destiladoConIngrediente).toLowerCase().includes(term))
        );
        return noProdMatch || inspMatch || obsMatch || itemMatch;
      }

      return true;
    });
  }, [destilacionRecords, statusFilter, localClaseFilter, search]);

  // =========================================================================
  // GESTIÓN DE ACCIONES Y EVENTOS DEL FORMULARIO
  // =========================================================================

  // Agregar un nuevo alambique / carga
  const handleAddItem = () => {
    const today = new Date().toISOString().split('T')[0];
    const currentList = destilacionItems;
    const nextNum = currentList.length + 1;
    const prevItem = currentList[currentList.length - 1];

    const newItem = {
      id: Date.now(),
      fechaSegunda: prevItem?.fechaSegunda || today,
      destiladorNum: `Alambique ${nextNum < 10 ? '0' + nextNum : nextNum}`,
      tipoAlambique: "",
      nuevoLote: "",
      volADestilar: "",
      esDestiladoCon: false,
      destiladoConIngrediente: "",
      destiladoConIngredientes: [],
      puntasVol: "",
      puntasAlc: "",
      mezcalVol: "",
      mezcalAlc: "",
      colasVol: "",
      colasAlc: ""
    };

    setFormData(prev => ({
      ...prev,
      items: [...currentList, newItem]
    }));
  };

  // Eliminar un alambique / carga
  const handleRemoveItem = (id) => {
    if (destilacionItems.length <= 1) {
      Swal.fire({
        title: "Atención",
        text: "Debe registrar al menos un alambique / destilación en la bitácora.",
        icon: "info",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).filter(item => item.id !== id)
    }));
  };

  // Actualizar un campo específico
  const handleItemChange = (id, field, value) => {
    setFormData(prev => {
      const currentList = Array.isArray(prev?.items) && prev.items.length > 0 ? prev.items : destilacionItems;
      const updatedList = currentList.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };

        // Clamp porcentajes de alcohol entre 0 y 100%
        if (field === 'puntasAlc' || field === 'mezcalAlc' || field === 'colasAlc') {
          const p = parseFloat(String(value).replace(/[^\d.]/g, '')) || 0;
          if (p > 100) updated[field] = "100";
        }

        return updated;
      });

      return {
        ...prev,
        items: updatedList
      };
    });
  };

  // Manejo de ingredientes de "Destilado con"
  const handleAddIngredient = (itemId, newIng) => {
    if (!newIng || !newIng.trim()) return;
    const cleanIng = newIng.trim();

    setFormData(prev => {
      const currentList = Array.isArray(prev?.items) && prev.items.length > 0 ? prev.items : destilacionItems;
      const updated = currentList.map(item => {
        if (item.id !== itemId) return item;
        const list = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
        if (list.includes(cleanIng)) return item;
        const newList = [...list, cleanIng];
        return {
          ...item,
          esDestiladoCon: true,
          destiladoConIngredientes: newList,
          destiladoConIngrediente: newList.join(', ')
        };
      });
      return { ...prev, items: updated };
    });
  };

  const handleRemoveIngredient = (itemId, ingToRemove) => {
    setFormData(prev => {
      const currentList = Array.isArray(prev?.items) && prev.items.length > 0 ? prev.items : destilacionItems;
      const updated = currentList.map(item => {
        if (item.id !== itemId) return item;
        const list = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
        const newList = list.filter(ing => ing !== ingToRemove);
        return {
          ...item,
          destiladoConIngredientes: newList,
          destiladoConIngrediente: newList.join(', ')
        };
      });
      return { ...prev, items: updated };
    });
  };

  const handleEditRecord = (rec) => {
    const norm = String(rec.status || '').toLowerCase().trim();
    if (norm !== "borrador" && norm !== "rechazado" && norm !== "rechazada") {
      Swal.fire({
        title: "Acción restringida",
        text: "Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.",
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    const matchedCoccion = (Array.isArray(coccionRecords) ? coccionRecords : []).find(c =>
      (rec.noProduccion && String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase() === String(rec.noProduccion).trim().toLowerCase())
    );
    const crudoFromCoccion = extractKgAgaveCrudo(matchedCoccion);

    setFormData({
      id: rec.id || rec.rawId,
      rawId: rec.rawId || rec.id,
      noProduccion: rec.noProduccion || "",
      id_bitacora_molienda: rec.id_bitacora_molienda || rec.idBitacoraMolienda || null,
      kgAgave: rec.kgAgave || rec.kg_agave || (crudoFromCoccion > 0 ? String(crudoFromCoccion) : ""),
      pctArt: rec.pctArt || rec.pct_art || matchedCoccion?.pctArt || matchedCoccion?.pct_art || "",
      rendimientoReal: rec.rendimientoReal || rec.rendimiento_real || "",
      rendimientoTeorico: rec.rendimientoTeorico || rec.rendimiento_teorico || "",
      eficienciaPct: rec.eficienciaPct || rec.eficiencia_pct || "",
      observaciones: rec.observaciones || "",
      status: rec.status || "borrador",
      observacionesCorreccion: rec.observacionesCorreccion || rec.observaciones_correccion || "",
      items: (rec.items && Array.isArray(rec.items) && rec.items.length > 0) ? rec.items.map((it, idx) => ({
        id: it.id || Date.now() + idx,
        fechaSegunda: it.fechaSegunda || it.fecha_segunda || "",
        destiladorNum: it.destiladorNum || it.destilador_num || `Alambique ${idx + 1 < 10 ? '0' + (idx + 1) : idx + 1}`,
        tipoAlambique: it.tipoAlambique || it.tipo_alambique || "",
        nuevoLote: it.nuevoLote || it.nuevo_lote || "",
        volADestilar: it.volADestilar ? String(it.volADestilar).replace(/[^\d.]/g, '') : "",
        esDestiladoCon: Boolean(it.esDestiladoCon || it.destiladoConIngrediente || (it.destiladoConIngredientes && it.destiladoConIngredientes.length > 0)),
        destiladoConIngrediente: it.destiladoConIngrediente || "",
        destiladoConIngredientes: it.destiladoConIngredientes || (it.destiladoConIngrediente ? it.destiladoConIngrediente.split(', ').filter(Boolean) : []),
        puntasVol: it.puntasVol ? String(it.puntasVol).replace(/[^\d.]/g, '') : (it.puntas ? String(it.puntas).split(' ')[0] : ""),
        puntasAlc: it.puntasAlc ? String(it.puntasAlc).replace(/[^\d.]/g, '') : "",
        mezcalVol: it.mezcalVol ? String(it.mezcalVol).replace(/[^\d.]/g, '') : (it.mezcal ? String(it.mezcal).split(' ')[0] : ""),
        mezcalAlc: it.mezcalAlc ? String(it.mezcalAlc).replace(/[^\d.]/g, '') : "",
        colasVol: it.colasVol ? String(it.colasVol).replace(/[^\d.]/g, '') : (it.colas ? String(it.colas).split(' ')[0] : ""),
        colasAlc: it.colasAlc ? String(it.colasAlc).replace(/[^\d.]/g, '') : ""
      })) : [
        {
          id: Date.now(),
          fechaSegunda: new Date().toISOString().split('T')[0],
          destiladorNum: "Alambique 01",
          tipoAlambique: "",
          nuevoLote: "",
          volADestilar: "",
          esDestiladoCon: false,
          destiladoConIngrediente: "",
          destiladoConIngredientes: [],
          puntasVol: "",
          puntasAlc: "",
          mezcalVol: "",
          mezcalAlc: "",
          colasVol: "",
          colasAlc: ""
        }
      ]
    });
    setIsFormOpen(true);
  };

  return (
    <div className="animate-fadeIn">
      {/* =========================================================================
          1. PANEL SUPERIOR DE KPIS CONSOLIDADOS DE DESTILACIÓN
      ========================================================================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: "14px",
          marginBottom: "20px"
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            border: "1.5px solid #E2E8F0",
            borderRadius: "16px",
            padding: "16px 18px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: "rgba(37, 99, 235, 0.12)",
              color: "#2563EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px"
            }}
          >
            <WineBarIcon />
          </div>
          <div>
            <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Total Destilaciones
            </span>
            <h3 style={{ margin: "2px 0 0 0", fontSize: "20px", fontWeight: 900, color: "var(--text-primary)" }}>
              {kpiStats.totalRecords} <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>lotes</span>
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1.5px solid #E2E8F0",
            borderRadius: "16px",
            padding: "16px 18px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: "rgba(124, 58, 237, 0.12)",
              color: "#7C3AED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px"
            }}
          >
            <WaterDropIcon />
          </div>
          <div>
            <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              1ra Dest. Cargada (Ordinario)
            </span>
            <h3 style={{ margin: "2px 0 0 0", fontSize: "20px", fontWeight: 900, color: "#7C3AED" }}>
              {kpiStats.totalOrdinarioCargado.toLocaleString()} <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>L</span>
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1.5px solid #E2E8F0",
            borderRadius: "16px",
            padding: "16px 18px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: "rgba(16, 185, 129, 0.12)",
              color: "#059669",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px"
            }}
          >
            <ScienceIcon />
          </div>
          <div>
            <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Mezcal Refinado Obtenido
            </span>
            <h3 style={{ margin: "2px 0 0 0", fontSize: "20px", fontWeight: 900, color: "#059669" }}>
              {kpiStats.totalMezcalObtenido.toLocaleString()} <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>L</span>
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1.5px solid #E2E8F0",
            borderRadius: "16px",
            padding: "16px 18px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: "rgba(184, 115, 51, 0.12)",
              color: "var(--copper)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px"
            }}
          >
            <LocalFireDepartmentIcon />
          </div>
          <div>
            <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Grado Alc. Ponderado
            </span>
            <h3 style={{ margin: "2px 0 0 0", fontSize: "20px", fontWeight: 900, color: "var(--copper)" }}>
              {kpiStats.avgAlcGlobal}° <span style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--text-muted)" }}>Alc. Vol. ({kpiStats.rendimientoGlobal}% rend.)</span>
            </h3>
          </div>
        </div>
      </div>

      {/* =========================================================================
          2. HEADER DE HISTORIAL, BUSCADOR Y FILTROS MODERNOS
      ========================================================================= */}
      <div className="destilacion-toolbar-container">
        <div className="destilacion-toolbar-top">
          <div className="destilacion-toolbar-title-box">
            <h2>
              <WineBarIcon style={{ color: "#2563EB", fontSize: 22 }} />
              Destilación & Rectificación (Sección IV)
            </h2>
            <p>
              Bitácora de 2da destilación, cortes de rectificación (Puntas, Mezcal Corazón, Colas) y clasificación especial
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (onOpenNewDestilacion) {
                onOpenNewDestilacion();
              } else {
                const today = new Date().toISOString().split('T')[0];
                setFormData({
                  id: null,
                  rawId: null,
                  noProduccion: "",
                  id_bitacora_molienda: null,
                  observaciones: "",
                  status: "borrador",
                  items: [
                    {
                      id: Date.now(),
                      fechaSegunda: today,
                      destiladorNum: "Alambique 01",
                      tipoAlambique: "",
                      nuevoLote: "",
                      volADestilar: "",
                      esDestiladoCon: false,
                      destiladoConIngrediente: "",
                      destiladoConIngredientes: [],
                      puntasVol: "",
                      puntasAlc: "65.0",
                      mezcalVol: "",
                      mezcalAlc: "48.5",
                      colasVol: "",
                      colasAlc: "20.0"
                    }
                  ]
                });
                setIsFormOpen(true);
              }
            }}
            className="destilacion-btn-new"
          >
            <AddIcon style={{ fontSize: 18 }} />
            <span>Nuevo Registro de Destilación</span>
          </button>
        </div>

        <div className="destilacion-toolbar-controls">
          {/* Búsqueda inteligente */}
          <div className="destilacion-search-box">
            <SearchIcon className="destilacion-search-icon" />
            <input
              type="text"
              placeholder="Buscar por lote, alambique, ingrediente..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="destilacion-search-clear"
                onClick={() => handleSearchChange("")}
                title="Limpiar búsqueda"
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>

          {/* Filtro por estado con icono */}
          <div className="destilacion-filter-wrapper">
            <FilterListIcon className="destilacion-filter-icon" />
            <select
              value={statusFilter}
              onChange={e => handleStatusFilterChange(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todos los Estados</option>
              <option value="aceptada">Aceptadas por UI</option>
              <option value="enviada">Enviadas / En Revisión</option>
              <option value="borrador">Borradores</option>
              <option value="rechazada">Rechazadas (Corrección)</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </div>

          {/* Filtro por clasificación */}
          <div className="destilacion-filter-wrapper">
            <WineBarIcon className="destilacion-filter-icon" style={{ color: "#7C3AED" }} />
            <select
              value={localClaseFilter}
              onChange={e => setLocalClaseFilter(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todas las Clases</option>
              <option value="joven">Joven / Blanco</option>
              <option value="destilado_con">Destilado con...</option>
            </select>
          </div>

          {/* Botón Reset si hay filtros activos */}
          {(search || statusFilter !== "todos" || localClaseFilter !== "todos") && (
            <button
              type="button"
              className="destilacion-btn-reset"
              onClick={() => {
                handleSearchChange("");
                handleStatusFilterChange("todos");
                setLocalClaseFilter("todos");
              }}
              title="Restablecer todos los filtros"
            >
              <CloseIcon style={{ fontSize: 15 }} />
              <span>Limpiar Filtros</span>
            </button>
          )}
        </div>
      </div>

      {/* =========================================================================
          3. TABLA HISTÓRICA DE DESTILACIÓN
      ========================================================================= */}
      {filteredRecords.length === 0 ? (
        <div className="empty-history-card">
          <InboxIcon className="empty-history-icon" />
          <h3>{search || statusFilter !== "todos" || localClaseFilter !== "todos" ? "No se encontraron resultados" : "No hay bitácoras de destilación registradas"}</h3>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
            {search || statusFilter !== "todos" || localClaseFilter !== "todos"
              ? "No se hallaron registros coincidentes con los filtros aplicados."
              : "Actualmente no existen registros guardados. Haga clic en 'Nuevo Registro de Destilación' para capturar su primer lote."}
          </p>
        </div>
      ) : (
        <div className="table-responsive-container">
          <table className="custom-table-bitacora">
            <thead>
              <tr>
                <th>No. Producción</th>
                <th>Alambiques / Cargas</th>
                <th>Carga Ordinario</th>
                <th>Mezcal Refinado Obtenido</th>
                <th>Clasificación</th>
                <th>Observaciones</th>
                <th>V.Bo. Inspector</th>
                <th>Estado</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((rec, i) => {
                const numItems = rec.items ? rec.items.length : 1;
                const tieneDestiladoCon = rec.items
                  ? rec.items.some(item => item.esDestiladoCon || (item.destiladoConIngredientes && item.destiladoConIngredientes.length > 0))
                  : Boolean(rec.esDestiladoCon);

                // Calcular volumen total y mezcal total del registro
                let totalCargaRec = 0;
                let totalMezcalRec = 0;
                let totalAlcVolRec = 0;

                if (rec.items && Array.isArray(rec.items) && rec.items.length > 0) {
                  rec.items.forEach(it => {
                    const c = parseFloat(String(it.volADestilar || 0).replace(/[^\d.]/g, '')) || 0;
                    const m = parseFloat(String(it.mezcalVol || 0).replace(/[^\d.]/g, '')) || 0;
                    const a = parseFloat(String(it.mezcalAlc || 0).replace(/[^\d.]/g, '')) || 0;
                    totalCargaRec += c;
                    totalMezcalRec += m;
                    totalAlcVolRec += (m * a);
                  });
                } else {
                  totalCargaRec = parseFloat(String(rec.volADestilar || rec.totalVolCargado || 0).replace(/[^\d.]/g, '')) || 0;
                  totalMezcalRec = parseFloat(String(rec.mezcal || rec.totalMezcalObtenido || 0).replace(/[^\d.]/g, '')) || 0;
                  totalAlcVolRec = totalMezcalRec * (parseFloat(String(rec.avgAlcMezcal || 48.0)) || 48.0);
                }

                const avgAlcRec = totalMezcalRec > 0 ? (totalAlcVolRec / totalMezcalRec).toFixed(1) : "48.0";

                return (
                  <tr key={rec.id || i}>
                    <td className="font-bold-primary">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 800, color: "var(--text-primary)" }}>{rec.noProduccion}</span>
                        {rec.fechaSegunda && (
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{rec.fechaSegunda}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ background: "rgba(37, 99, 235, 0.08)", padding: "4px 10px", borderRadius: "8px", fontSize: "12px", color: "#1D4ED8", fontWeight: 700 }}>
                        {numItems} {numItems === 1 ? 'alambique' : 'alambiques'}
                      </span>
                    </td>
                    <td>
                      <strong style={{ color: "var(--text-primary)", fontWeight: 700 }}>
                        {totalCargaRec > 0 ? `${totalCargaRec.toLocaleString()} L` : "-"}
                      </strong>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ color: "#059669", fontWeight: 900, fontSize: "14px" }}>
                          {totalMezcalRec > 0 ? `${totalMezcalRec.toLocaleString()} L` : (rec.mezcal || "-")}
                        </strong>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600 }}>
                          Grad. {avgAlcRec}° Alc. Vol.
                        </span>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "10px",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        background: tieneDestiladoCon ? "rgba(230, 126, 34, 0.12)" : "rgba(46, 204, 113, 0.12)",
                        color: tieneDestiladoCon ? "#D97706" : "#059669",
                        border: tieneDestiladoCon ? "1px solid rgba(230, 126, 34, 0.3)" : "1px solid rgba(46, 204, 113, 0.3)"
                      }}>
                        {tieneDestiladoCon ? "Destilado con" : "Blanco / Joven"}
                      </span>
                    </td>
                    <td style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.observaciones}>
                      {rec.observaciones || "-"}
                    </td>
                    <td>
                      <span className="inspector-tag">{rec.inspector || 'Pendiente de V.Bo.'}</span>
                    </td>
                    <td>
                      {renderDestilacionStatusBadge(rec.status || rec.statusUI)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          className="btn-action-icon view"
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        {(rec.status === "borrador" || rec.status === "rechazado" || rec.status === "rechazada") && (
                          <button
                            type="button"
                            onClick={() => handleEditRecord(rec)}
                            title="Editar Borrador / Corregir"
                            className="btn-action-icon edit"
                          >
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        )}

                        {rec.status !== "cancelada" && rec.status !== "cancelado" && rec.status !== "aprobado" && rec.status !== "aceptada" && rec.status !== "aceptado" && handleCancelDestilacion && (
                          <button
                            type="button"
                            onClick={() => handleCancelDestilacion(rec)}
                            title="Cancelar Bitácora"
                            className="btn-action-icon cancel"
                          >
                            <CancelIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* =========================================================================
          MODAL 1: DETALLE COMPLETO DE DESTILACIÓN VIA PORTAL
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "980px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-destilacion" style={{ background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)", color: "white" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row">
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    SECCIÓN IV: DESTILACIÓN & RECTIFICACIÓN
                  </span>
                  {renderModalHeaderStatusBadge(selectedRecord.status || selectedRecord.statusUI)}
                </div>
                <h1 className="detail-modal-title" style={{ color: "white" }}>
                  Lote: {selectedRecord.noProduccion || "Sin Número"}
                </h1>
                <p className="detail-modal-subtitle" style={{ color: "rgba(255,255,255,0.85)" }}>
                  Segunda destilación, cortes de rectificación (Puntas, Mezcal Corazón, Colas) y rendimientos finales
                </p>
              </div>

              <button
                onClick={() => setSelectedRecord(null)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Modal */}
            <div className="detail-modal-body">
              <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

                {/* Banner de Observaciones de Corrección si fue Rechazado */}
                {(selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion) && (
                  <div style={{ background: "#FEF3C7", border: "1.5px solid #F59E0B", borderRadius: "14px", padding: "16px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <ReportProblemIcon style={{ color: "#D97706", fontSize: 24, marginTop: "2px" }} />
                    <div>
                      <h4 style={{ margin: "0 0 4px 0", color: "#92400E", fontSize: "14px", fontWeight: 800 }}>
                        Observaciones de Corrección de la Unidad de Inspección:
                      </h4>
                      <p style={{ margin: 0, color: "#78350F", fontSize: "13px", lineHeight: "1.5" }}>
                        {selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion}
                      </p>
                    </div>
                  </div>
                )}

                {/* Resumen Ejecutivo del Lote */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
                    border: "1.5px solid #BFDBFE",
                    borderRadius: "16px",
                    padding: "16px 20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "14px"
                  }}
                >
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Lote Producción</span>
                    <h4 style={{ margin: "2px 0 0 0", color: "var(--text-primary)", fontSize: "15px", fontWeight: 800 }}>{selectedRecord.noProduccion}</h4>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Alambiques Registrados</span>
                    <h4 style={{ margin: "2px 0 0 0", color: "#1D4ED8", fontSize: "15px", fontWeight: 800 }}>
                      {selectedRecord.items?.length || 1} alambiques
                    </h4>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Mezcal Obtenido</span>
                    <h4 style={{ margin: "2px 0 0 0", color: "#059669", fontSize: "15px", fontWeight: 900 }}>
                      {selectedRecord.mezcal || (selectedRecord.items && selectedRecord.items[0] ? `${selectedRecord.items[0].mezcalVol || 0} L` : "-")}
                    </h4>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Inspector Asignado</span>
                    <h4 style={{ margin: "2px 0 0 0", color: "var(--text-primary)", fontSize: "13px", fontWeight: 700 }}>
                      {selectedRecord.inspector || 'Pendiente de V.Bo.'}
                    </h4>
                  </div>
                </div>

                {/* Grid de Destilaciones / Alambiques Registrados */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {(selectedRecord.items || []).map((item, index) => {
                    const volCarga = parseFloat(String(item.volADestilar || 0).replace(/[^\d.]/g, '')) || 0;
                    const volMez = parseFloat(String(item.mezcalVol || 0).replace(/[^\d.]/g, '')) || 0;
                    const rendAlambique = volCarga > 0 ? Math.round((volMez / volCarga) * 100) : 0;
                    const tieneIngredientes = item.esDestiladoCon || (item.destiladoConIngredientes && item.destiladoConIngredientes.length > 0) || Boolean(item.destiladoConIngrediente);
                    const listIng = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);

                    return (
                      <div
                        key={item.id || index}
                        style={{
                          background: "#FFFFFF",
                          border: "1.5px solid #E2E8F0",
                          borderRadius: "16px",
                          padding: "18px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", paddingBottom: "10px", borderBottom: "1px solid #E2E8F0", flexWrap: "wrap", gap: "10px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ background: "rgba(37, 99, 235, 0.12)", color: "#1D4ED8", fontWeight: 800, padding: "5px 14px", borderRadius: "8px", fontSize: "13px" }}>
                              {item.destiladorNum || `Alambique #${index + 1}`}
                            </span>
                            <span style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 600 }}>
                              {item.tipoAlambique || "-"}
                            </span>
                          </div>

                          <span style={{
                            padding: "4px 12px",
                            borderRadius: "10px",
                            fontSize: "11.5px",
                            fontWeight: 700,
                            background: tieneIngredientes ? "rgba(230, 126, 34, 0.12)" : "rgba(46, 204, 113, 0.12)",
                            color: tieneIngredientes ? "#D97706" : "#059669",
                            border: tieneIngredientes ? "1px solid rgba(230, 126, 34, 0.3)" : "1px solid rgba(46, 204, 113, 0.3)"
                          }}>
                            {tieneIngredientes ? "Destilado con ingredientes especiales" : "Blanco / Joven"}
                          </span>
                        </div>

                        {/* Si tiene ingredientes de Destilado con */}
                        {tieneIngredientes && listIng.length > 0 && (
                          <div style={{ marginBottom: "14px", background: "rgba(184, 115, 51, 0.06)", border: "1px solid rgba(184, 115, 51, 0.2)", borderRadius: "10px", padding: "10px 14px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--copper)", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                              Ingredientes Adicionados en Destilación:
                            </span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                              {listIng.map((ing, ingIdx) => (
                                <span
                                  key={ingIdx}
                                  style={{ background: "#FFFFFF", color: "var(--copper)", border: "1px solid rgba(184, 115, 51, 0.4)", borderRadius: "6px", padding: "2px 8px", fontSize: "11.5px", fontWeight: 700 }}
                                >
                                  🍃 {ing}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Métricas de Cortes de Rectificación */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
                          <div className="integrated-input-box">
                            <span className="integrated-input-label">FECHA 2DA DESTILACIÓN</span>
                            <div className="integrated-display-value">{item.fechaSegunda || "-"}</div>
                          </div>

                          <div className="integrated-input-box">
                            <span className="integrated-input-label">CARGA DE ORDINARIO</span>
                            <div className="integrated-display-value">
                              <strong style={{ color: "var(--text-primary)", fontWeight: 800 }}>
                                {item.volADestilar ? `${Number(item.volADestilar).toLocaleString()} L` : "-"}
                              </strong>
                            </div>
                          </div>

                          <div className="integrated-input-box" style={{ background: "rgba(245, 158, 11, 0.05)", borderColor: "rgba(245, 158, 11, 0.3)" }}>
                            <span className="integrated-input-label" style={{ color: "#D97706" }}>PUNTAS / CABEZAS</span>
                            <div className="integrated-display-value">
                              <strong style={{ color: "#D97706", fontWeight: 800 }}>
                                {item.puntasVol ? `${item.puntasVol} L (${item.puntasAlc || 0}%)` : (item.puntas || "-")}
                              </strong>
                            </div>
                          </div>

                          <div className="integrated-input-box" style={{ background: "rgba(37, 99, 235, 0.06)", borderColor: "rgba(37, 99, 235, 0.4)" }}>
                            <span className="integrated-input-label" style={{ color: "#1D4ED8" }}>MEZCAL REFINADO (CORAZÓN)</span>
                            <div className="integrated-display-value">
                              <strong style={{ color: "#1D4ED8", fontWeight: 900, fontSize: "14.5px" }}>
                                {item.mezcalVol ? `${item.mezcalVol} L (${item.mezcalAlc || 0}%)` : (item.mezcal || "-")}
                              </strong>
                            </div>
                          </div>

                          <div className="integrated-input-box" style={{ background: "rgba(124, 58, 237, 0.05)", borderColor: "rgba(124, 58, 237, 0.3)" }}>
                            <span className="integrated-input-label" style={{ color: "#7C3AED" }}>COLAS</span>
                            <div className="integrated-display-value">
                              <strong style={{ color: "#7C3AED", fontWeight: 800 }}>
                                {item.colasVol ? `${item.colasVol} L (${item.colasAlc || 0}%)` : (item.colas || "-")}
                              </strong>
                            </div>
                          </div>

                          <div className="integrated-input-box">
                            <span className="integrated-input-label">RENDIMIENTO RECTIFICACIÓN</span>
                            <div className="integrated-display-value">
                              <strong style={{ color: "#059669", fontWeight: 800 }}>
                                {rendAlambique}%
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Rendimientos y Eficiencia en Detalle (Cálculo Oficial) */}
                {selectedRecordCalc && (
                  <div style={{ background: "#FFFFFF", border: "1.5px solid #CBD5E1", borderRadius: "14px", padding: "16px 18px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #E2E8F0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <CalculateIcon style={{ color: "#065F46", fontSize: 20 }} />
                        <span style={{ fontSize: "13px", fontWeight: 900, color: "#065F46", textTransform: "uppercase" }}>
                          Rendimientos y Eficiencia de Destilación (Base 45% Alc. Vol.)
                        </span>
                      </div>
                      <span style={{ fontSize: "11px", color: "#64748B", fontWeight: 600 }}>
                        Agave: <strong>{selectedRecordCalc.kgAgave.toLocaleString()} kg</strong> | % ART: <strong>{selectedRecordCalc.pctArt}%</strong>
                      </span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                      <div style={{ background: "#F8FAFC", border: "1px solid #BFDBFE", borderRadius: "10px", padding: "12px", textAlign: "center" }}>
                        <span style={{ fontSize: "10.5px", fontWeight: 800, color: "#1E40AF", display: "block", textTransform: "uppercase" }}>
                          Rendimiento Real (R₁)
                        </span>
                        <div style={{ fontSize: "18px", fontWeight: 900, color: "#1E40AF", marginTop: "4px" }}>
                          {selectedRecordCalc.r1 > 0 ? selectedRecordCalc.r1.toFixed(2) : "--"} <small style={{ fontSize: "11px" }}>Kg/L</small>
                        </div>
                        <span style={{ fontSize: "10px", color: "#64748B" }}>Kg Agave / Total Litros a 45%</span>
                      </div>

                      <div style={{ background: "#F8FAFC", border: "1px solid #A7F3D0", borderRadius: "10px", padding: "12px", textAlign: "center" }}>
                        <span style={{ fontSize: "10.5px", fontWeight: 800, color: "#065F46", display: "block", textTransform: "uppercase" }}>
                          Rendimiento Teórico (R₂)
                        </span>
                        <div style={{ fontSize: "18px", fontWeight: 900, color: "#065F46", marginTop: "4px" }}>
                          {selectedRecordCalc.r2 > 0 ? selectedRecordCalc.r2.toFixed(2) : "--"} <small style={{ fontSize: "11px" }}>Kg/L</small>
                        </div>
                        <span style={{ fontSize: "10px", color: "#64748B" }}>Rendimiento óptimo según % ART</span>
                      </div>

                      <div style={{ background: "#F8FAFC", border: `1px solid ${selectedRecordCalc.eficiencia >= 70 ? "#A7F3D0" : (selectedRecordCalc.eficiencia >= 50 ? "#FDE68A" : "#FECACA")}`, borderRadius: "10px", padding: "12px", textAlign: "center" }}>
                        <span style={{ fontSize: "10.5px", fontWeight: 800, color: "#0F172A", display: "block", textTransform: "uppercase" }}>
                          % Eficiencia (E)
                        </span>
                        <div style={{ fontSize: "18px", fontWeight: 900, color: selectedRecordCalc.eficiencia >= 70 ? "#059669" : (selectedRecordCalc.eficiencia >= 50 ? "#D97706" : "#DC2626"), marginTop: "4px" }}>
                          {selectedRecordCalc.eficiencia > 0 ? `${selectedRecordCalc.eficiencia.toFixed(1)}%` : "--"}
                        </div>
                        <span style={{ fontSize: "10px", color: "#64748B" }}>(Rend. Teórico / Rend. Real) × 100</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Observaciones Generales */}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "16px" }}>
                  <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>OBSERVACIONES GENERALES</span>
                  <p style={{ margin: "4px 0 0 0", color: "var(--text-primary)", fontSize: "13px", lineHeight: "1.5" }}>
                    {selectedRecord.observaciones || "Sin observaciones registradas en este lote de destilación."}
                  </p>
                </div>

              </div>
            </div>

            {/* Footer del Modal */}
            <div className="detail-modal-footer">
              {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado" || selectedRecord.status === "rechazada") && (
                <button
                  type="button"
                  onClick={() => {
                    const r = selectedRecord;
                    setSelectedRecord(null);
                    handleEditRecord(r);
                  }}
                  className="btn-submit"
                  style={{ background: "#F59E0B", color: "#FFFFFF", padding: "9px 20px", fontWeight: 700 }}
                >
                  <EditIcon style={{ fontSize: 18 }} />
                  <span>Editar y Corregir</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="btn-secondary-custom"
                style={{ padding: "9px 22px" }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: FORMULARIO ULTRA-COMPACTO Y RÁPIDO DE DESTILACIÓN VIA PORTAL
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1000px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-destilacion" style={{ background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    {formData.id || formData.rawId ? 'EDITAR BITÁCORA' : 'NUEVO REGISTRO'}
                  </span>
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    SECCIÓN IV: DESTILACIÓN
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  {formData.id || formData.rawId ? `Editar Destilación: ${formData.noProduccion || ''}` : 'Registro de Segunda Destilación & Rectificación'}
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Formulario */}
            <form onSubmit={e => {
              e.preventDefault();
              handleSaveDestilacion(e, "enviada", formSubmissionData);
            }} className="detail-modal-body" style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

                {/* 1. SELECCIÓN DE LOTE Y CONTROL COMPACTO DE SALDO */}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "14px", alignItems: "center" }}>
                    <div>
                      <label style={{ fontSize: "10.5px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "3px" }}>
                        NO. DE PRODUCCIÓN (LOTE MOLIENDA) *
                      </label>
                      <select
                        id="destilacionNoProduccion"
                        value={formData.noProduccion || ""}
                        onChange={e => {
                          const val = e.target.value;
                          const matchedMolienda = availableMoliendas.find(m =>
                            val && (
                              m.noProduccion === val ||
                              m.id === val ||
                              String(m.rawId) === String(val) ||
                              String(m.idBitacoraMolienda) === String(val)
                            )
                          );

                          // Buscar cocción relacionada en todos los orígenes posibles
                          const targetCoccionId = matchedMolienda?.id_bitacora_coccion || matchedMolienda?.idBitacoraCoccion || matchedMolienda?.id_coccion;
                          const targetNoProd = String(val || matchedMolienda?.noProduccion || '').trim().toLowerCase();

                          const matchedCoccion = matchedMolienda?.coccion || (Array.isArray(coccionRecords) ? coccionRecords : []).find(c => {
                            const cRawId = String(c.rawId || c.idBitacoraCoccion || c.id_bitacora_coccion || c.id || '').trim();
                            const cNoProd = String(c.noProduccion || c.no_produccion || c.id || '').trim().toLowerCase();
                            if (targetCoccionId && cRawId && cRawId === String(targetCoccionId).trim()) return true;
                            if (targetNoProd && cNoProd && (cNoProd === targetNoProd || cNoProd.includes(targetNoProd) || targetNoProd.includes(cNoProd))) return true;
                            return false;
                          }) || null;

                          let autoKgCrudo = extractKgAgaveCrudo(matchedCoccion);
                          if (!autoKgCrudo && matchedMolienda?.coccion) {
                            autoKgCrudo = extractKgAgaveCrudo(matchedMolienda.coccion);
                          }
                          const autoArt = matchedCoccion ? (matchedCoccion.pctArt || matchedCoccion.pct_art) : (matchedMolienda?.coccion?.pctArt || "");

                          // Generar automáticamente los 2 registros (uno de Mezcal y uno de Ordinario)
                          const today = new Date().toISOString().split('T')[0];
                          const newItems = matchedMolienda
                            ? createDestilacionItemsFromMolienda(matchedMolienda, formData.fechaSegunda || today)
                            : createDestilacionItemsFromMolienda(null, formData.fechaSegunda || today);

                          setFormData(prev => ({
                            ...prev,
                            noProduccion: val,
                            id_bitacora_molienda: matchedMolienda ? (matchedMolienda.rawId || matchedMolienda.id) : null,
                            kgAgave: autoKgCrudo > 0 ? String(autoKgCrudo) : (prev.kgAgave || ""),
                            pctArt: autoArt ? String(autoArt) : (prev.pctArt || "24.0"),
                            items: newItems
                          }));
                        }}
                        className="integrated-select-control"
                        style={{ height: "36px", fontSize: "13px", fontWeight: 700 }}
                        required
                      >
                        <option value="">
                          {availableMoliendas.length === 0 ? "-- No hay lotes de molienda disponibles --" : "-- Seleccionar Lote de Molienda (Obligatorio) --"}
                        </option>
                        {availableMoliendas.map((r, idx) => {
                          const { totalVolOrdinario: rTotal, volDisponible: rDisp, isAgotado: rAgotado, numTinas } = getMoliendaDestilacionBalance(r, destilacionRecords, formData.rawId || formData.id);
                          const { volMezcal: rMez, volOrdinario: rOrd } = extract1raDestilacionVolumes(r);
                          const isCurrent = formData.noProduccion === r.noProduccion;
                          const tCount = numTinas || (Array.isArray(r.items) ? r.items.length : 0);
                          const tinasLabel = tCount > 0 ? ` (${tCount} ${tCount === 1 ? 'Tina' : 'Tinas'})` : '';
                          const breakdown = (rMez > 0 && rOrd > 0) ? ` [${rMez}L Mezcal + ${rOrd}L Ord]` : '';
                          const labelDisp = rTotal > 0 ? `Disp: ${rDisp.toLocaleString()} L${breakdown}` : 'Sin volumen registrado';
                          const disabled = rAgotado && !isCurrent;

                          return (
                            <option key={idx} value={r.noProduccion} disabled={disabled}>
                              {r.noProduccion} ({r.tipoMaguey || "Molienda"}){tinasLabel} — {disabled ? '[AGOTADO: 0 L disp.]' : labelDisp}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {formMolienda && (
                      <div style={{ display: "flex", gap: "12px", alignItems: "center", background: "#FFFFFF", border: "1px solid #CBD5E1", borderRadius: "10px", padding: "6px 14px", flexWrap: "wrap" }}>
                        <div>
                          <span style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>
                            1ra Dest. Total ({moliendaBalance.numTinas || (Array.isArray(formMolienda.items) ? formMolienda.items.length : 0)} Tinas)
                          </span>
                          <strong style={{ fontSize: "13.5px", color: "#1D4ED8" }}>{totalVolOrdinario.toLocaleString()} L</strong>
                        </div>
                        <div style={{ width: "1px", height: "24px", background: "#E2E8F0" }} />
                        <div>
                          <span style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Disponible</span>
                          <strong style={{ fontSize: "13.5px", color: "#059669" }}>{volDisponible.toLocaleString()} L</strong>
                        </div>
                        <div style={{ width: "1px", height: "24px", background: "#E2E8F0" }} />
                        <div>
                          <span style={{ fontSize: "10px", fontWeight: 700, color: isExcesoVol ? "#DC2626" : "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Cargando</span>
                          <strong style={{ fontSize: "13.5px", color: isExcesoVol ? "#DC2626" : "var(--text-primary)" }}>{formTotalVolADestilar.toLocaleString()} L</strong>
                        </div>
                        {(formKgAgave > 0 || detectedKgAgaveCrudo > 0) && (
                          <>
                            <div style={{ width: "1px", height: "24px", background: "#E2E8F0" }} />
                            <div>
                              <span style={{ fontSize: "10px", fontWeight: 700, color: "#1E40AF", textTransform: "uppercase", display: "block" }}>Agave Crudo (Cocción)</span>
                              <strong style={{ fontSize: "13.5px", color: "#1E40AF" }}>{(formKgAgave || detectedKgAgaveCrudo).toLocaleString()} kg</strong>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {isExcesoVol && (
                    <div style={{ marginTop: "8px", color: "#DC2626", fontSize: "11.5px", fontWeight: 700, display: "flex", alignItems: "center", gap: "5px" }}>
                      <ReportProblemIcon style={{ fontSize: 16 }} />
                      <span>Atención: El volumen total cargado ({formTotalVolADestilar.toLocaleString()} L) supera el saldo disponible ({volDisponible.toLocaleString()} L).</span>
                    </div>
                  )}
                </div>

                {/* 2. RESUMEN MÉTRICO COMPACTO EN VIVO */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(95px, 1fr))",
                    gap: "8px",
                    padding: "8px 14px",
                    alignItems: "center",
                    background: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: "12px"
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Carga Total</span>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: isExcesoVol ? "#DC2626" : "#1E40AF" }}>
                      {formTotalVolADestilar.toLocaleString()} <small style={{ fontSize: "9.5px" }}>L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "#D97706", textTransform: "uppercase", fontWeight: 800 }}>Puntas</span>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#D97706" }}>
                      {formTotalPuntas.toLocaleString()} <small style={{ fontSize: "9.5px" }}>L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "#059669", textTransform: "uppercase", fontWeight: 800 }}>Mezcal Ref.</span>
                    <div style={{ fontSize: "14px", fontWeight: 900, color: "#059669" }}>
                      {formTotalMezcal.toLocaleString()} <small style={{ fontSize: "9.5px" }}>L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "#1D4ED8", textTransform: "uppercase", fontWeight: 800 }}>° Alc. Ponderado</span>
                    <div style={{ fontSize: "14px", fontWeight: 900, color: "#1D4ED8" }}>
                      {formAvgAlcMezcal}°
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "#7C3AED", textTransform: "uppercase", fontWeight: 800 }}>Colas</span>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#7C3AED" }}>
                      {formTotalColas.toLocaleString()} <small style={{ fontSize: "9.5px" }}>L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center", borderLeft: "1px solid #E2E8F0", paddingLeft: "4px" }}>
                    <span style={{ fontSize: "9px", color: "#1E40AF", textTransform: "uppercase", fontWeight: 800 }}>R₁ Real</span>
                    <div style={{ fontSize: "14px", fontWeight: 900, color: "#1E40AF" }}>
                      {formR1 > 0 ? `${formR1.toFixed(1)}` : "--"} <small style={{ fontSize: "9px", color: "#64748B" }}>kg/L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: "#065F46", textTransform: "uppercase", fontWeight: 800 }}>R₂ Teórico</span>
                    <div style={{ fontSize: "14px", fontWeight: 900, color: "#065F46" }}>
                      {formR2 > 0 ? `${formR2.toFixed(1)}` : "--"} <small style={{ fontSize: "9px", color: "#64748B" }}>kg/L</small>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "9px", color: formEficiencia >= 70 ? "#059669" : (formEficiencia >= 50 ? "#D97706" : "#DC2626"), textTransform: "uppercase", fontWeight: 800 }}>% Eficiencia</span>
                    <div style={{ fontSize: "14px", fontWeight: 900, color: formEficiencia >= 70 ? "#059669" : (formEficiencia >= 50 ? "#D97706" : "#DC2626") }}>
                      {formEficiencia > 0 ? `${formEficiencia.toFixed(1)}%` : "--"}
                    </div>
                  </div>
                </div>

                {/* 3. ALAMBIQUES / DESTILADORES (DISEÑO ULTRA COMPACTO EN UNA SOLA TARJETA) */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {destilacionItems.map((item, index) => {
                    const isMezcalItem = item.tipoCarga === 'mezcal' || String(item.destiladorNum || '').toLowerCase().includes('mezcal');
                    const isOrdinarioItem = item.tipoCarga === 'ordinario' || String(item.destiladorNum || '').toLowerCase().includes('ordinario');

                    return (
                      <div
                        key={item.id || index}
                        style={{
                          background: "#FFFFFF",
                          border: isMezcalItem ? "1.5px solid #C4B5FD" : (isOrdinarioItem ? "1.5px solid #FDE68A" : "1.5px solid #CBD5E1"),
                          borderRadius: "12px",
                          padding: "12px 14px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
                        }}
                      >
                        {/* ENCABEZADO / IDENTIFICADOR DEL REGISTRO (MEZCAL VS ORDINARIO) */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", paddingBottom: "6px", borderBottom: "1px dashed #E2E8F0" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 800, color: "#475569" }}>
                              #{index + 1}
                            </span>
                            {isMezcalItem ? (
                              <span style={{ fontSize: "11px", background: "#EDE9FE", color: "#6D28D9", padding: "2px 10px", borderRadius: "12px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "5px", border: "1px solid #DDD6FE" }}>
                                💧 Registro: Vol. Mezcal (1ra Destilación)
                              </span>
                            ) : isOrdinarioItem ? (
                              <span style={{ fontSize: "11px", background: "#FEF3C7", color: "#B45309", padding: "2px 10px", borderRadius: "12px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "5px", border: "1px solid #FDE68A" }}>
                                🍯 Registro: Vol. Ordinario (A Rectificar)
                              </span>
                            ) : (
                              <span style={{ fontSize: "11px", background: "#F1F5F9", color: "#475569", padding: "2px 10px", borderRadius: "12px", fontWeight: 700, border: "1px solid #E2E8F0" }}>
                                ⚗️ Carga #{index + 1}
                              </span>
                            )}
                          </div>

                          {destilacionItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(item.id)}
                              style={{
                                background: "#FEE2E2",
                                color: "#DC2626",
                                border: "1px solid #FCA5A5",
                                borderRadius: "6px",
                                padding: "2px 8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "11px",
                                fontWeight: 700
                              }}
                              title="Eliminar este alambique"
                            >
                              <DeleteOutlineIcon style={{ fontSize: 15 }} />
                              <span>Eliminar</span>
                            </button>
                          )}
                        </div>

                        {/* FILA 1: DATOS PRINCIPALES DE OPERACIÓN */}
                        <div style={{ display: "grid", gridTemplateColumns: "140px 130px 1fr 120px 130px", gap: "8px", alignItems: "end", marginBottom: "8px" }}>
                          <div>
                            <label style={{ fontSize: "9.5px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "2px" }}>ALAMBIQUE</label>
                            <input
                              type="text"
                              placeholder="Alambique 01"
                              value={item.destiladorNum || ""}
                              onChange={e => handleItemChange(item.id, 'destiladorNum', e.target.value)}
                              style={{ height: "32px", fontWeight: 800, color: isMezcalItem ? "#6D28D9" : (isOrdinarioItem ? "#B45309" : "#1E40AF"), background: isMezcalItem ? "#F5F3FF" : (isOrdinarioItem ? "#FFFBEB" : "#EFF6FF"), border: "1px solid #CBD5E1", borderRadius: "6px", padding: "2px 8px", fontSize: "12px", width: "100%" }}
                            />
                          </div>

                          <div>
                            <label style={{ fontSize: "9.5px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "2px" }}>FECHA 2DA DEST.</label>
                            <input
                              type="date"
                              value={item.fechaSegunda || ""}
                              onChange={e => handleItemChange(item.id, 'fechaSegunda', e.target.value)}
                              style={{ height: "32px", border: "1px solid #CBD5E1", borderRadius: "6px", padding: "2px 6px", fontSize: "11.5px", width: "100%", outline: "none" }}
                            />
                          </div>

                          <div>
                            <label style={{ fontSize: "9.5px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "2px" }}>TIPO DE DESTILADOR</label>
                            <select
                              value={item.tipoAlambique || ""}
                              onChange={e => handleItemChange(item.id, 'tipoAlambique', e.target.value)}
                              className="integrated-select-control"
                              style={{ height: "32px", border: "1px solid #CBD5E1", borderRadius: "6px", padding: "2px 8px", fontSize: "12px", width: "100%", outline: "none", background: "#FFFFFF", cursor: "pointer" }}
                            >
                              <option value="">-- Seleccionar Tipo de Destilador --</option>
                              {TIPOS_DESTILADORES.map((t, tIdx) => (
                                <option key={tIdx} value={t}>{t}</option>
                              ))}
                              {item.tipoAlambique && !TIPOS_DESTILADORES.includes(item.tipoAlambique) && (
                                <option value={item.tipoAlambique}>{item.tipoAlambique}</option>
                              )}
                            </select>
                          </div>

                          <div>
                            <label style={{ fontSize: "9.5px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "2px" }}>NUEVO LOTE (OPC.)</label>
                            <input
                              type="text"
                              placeholder="Opcional"
                              value={item.nuevoLote || ""}
                              onChange={e => handleItemChange(item.id, 'nuevoLote', e.target.value)}
                              style={{ height: "32px", border: "1px solid #CBD5E1", borderRadius: "6px", padding: "2px 8px", fontSize: "11.5px", width: "100%", outline: "none" }}
                            />
                          </div>

                          <div>
                            <label style={{ fontSize: "9.5px", fontWeight: 800, color: isMezcalItem ? "#6D28D9" : (isOrdinarioItem ? "#B45309" : "#1E40AF"), textTransform: "uppercase", display: "block", marginBottom: "2px" }}>CARGA (L) *</label>
                            <div style={{ position: "relative" }}>
                              <input
                                type="number"
                                placeholder="500"
                                value={item.volADestilar || ""}
                                onChange={e => handleItemChange(item.id, 'volADestilar', e.target.value)}
                                required
                                style={{ height: "32px", border: isMezcalItem ? "1.5px solid #8B5CF6" : (isOrdinarioItem ? "1.5px solid #F59E0B" : "1.5px solid #3B82F6"), borderRadius: "6px", padding: "2px 24px 2px 8px", fontSize: "12.5px", fontWeight: 800, width: "100%", outline: "none", color: isMezcalItem ? "#6D28D9" : (isOrdinarioItem ? "#B45309" : "#1E40AF") }}
                              />
                              <span style={{ position: "absolute", right: "7px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#64748B", fontWeight: 700 }}>L</span>
                            </div>
                          </div>
                        </div>

                      {/* FILA 2: CORTES DE RECTIFICACIÓN (3 COLUMNAS LIMPIAS) */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.25fr 1fr", gap: "8px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "6px 10px", alignItems: "center" }}>
                        {/* PUNTAS */}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 800, color: "#D97706", width: "52px" }}>Puntas:</span>
                          <div style={{ display: "flex", gap: "4px", flex: 1 }}>
                            <div style={{ position: "relative", flex: 1 }}>
                              <input
                                type="number"
                                placeholder="Vol (L)"
                                value={item.puntasVol || ""}
                                onChange={e => handleItemChange(item.id, 'puntasVol', e.target.value)}
                                style={{ height: "28px", width: "100%", border: "1px solid #FCD34D", borderRadius: "5px", padding: "2px 18px 2px 6px", fontSize: "11.5px", fontWeight: 700, color: "#D97706", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#92400E", fontWeight: 700 }}>L</span>
                            </div>
                            <div style={{ position: "relative", width: "65px" }}>
                              <input
                                type="number"
                                step="0.1"
                                max="100"
                                placeholder="% Alc"
                                value={item.puntasAlc || ""}
                                onChange={e => handleItemChange(item.id, 'puntasAlc', e.target.value)}
                                style={{ height: "28px", width: "100%", border: "1px solid #CBD5E1", borderRadius: "5px", padding: "2px 16px 2px 6px", fontSize: "11.5px", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#64748B", fontWeight: 700 }}>%</span>
                            </div>
                          </div>
                        </div>

                        {/* MEZCAL REFINADO (CORAZÓN) */}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "#FFFFFF", padding: "3px 6px", borderRadius: "6px", border: "1.5px solid #86EFAC" }}>
                          <span style={{ fontSize: "11px", fontWeight: 900, color: "#059669", width: "75px" }}>Mezcal * :</span>
                          <div style={{ display: "flex", gap: "4px", flex: 1 }}>
                            <div style={{ position: "relative", flex: 1 }}>
                              <input
                                type="number"
                                placeholder="Volumen"
                                value={item.mezcalVol || ""}
                                onChange={e => handleItemChange(item.id, 'mezcalVol', e.target.value)}
                                required
                                style={{ height: "28px", width: "100%", border: "1px solid #86EFAC", borderRadius: "5px", padding: "2px 18px 2px 6px", fontSize: "12px", fontWeight: 900, color: "#059669", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#047857", fontWeight: 800 }}>L</span>
                            </div>
                            <div style={{ position: "relative", width: "70px" }}>
                              <input
                                type="number"
                                step="0.1"
                                max="100"
                                placeholder="° Alc"
                                value={item.mezcalAlc || ""}
                                onChange={e => handleItemChange(item.id, 'mezcalAlc', e.target.value)}
                                required
                                style={{ height: "28px", width: "100%", border: "1.5px solid #93C5FD", borderRadius: "5px", padding: "2px 16px 2px 6px", fontSize: "12px", fontWeight: 800, color: "#1D4ED8", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#1E40AF", fontWeight: 800 }}>%</span>
                            </div>
                          </div>
                        </div>

                        {/* COLAS */}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 800, color: "#7C3AED", width: "45px" }}>Colas:</span>
                          <div style={{ display: "flex", gap: "4px", flex: 1 }}>
                            <div style={{ position: "relative", flex: 1 }}>
                              <input
                                type="number"
                                placeholder="Vol (L)"
                                value={item.colasVol || ""}
                                onChange={e => handleItemChange(item.id, 'colasVol', e.target.value)}
                                style={{ height: "28px", width: "100%", border: "1px solid #DDD6FE", borderRadius: "5px", padding: "2px 18px 2px 6px", fontSize: "11.5px", fontWeight: 700, color: "#7C3AED", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#6D28D9", fontWeight: 700 }}>L</span>
                            </div>
                            <div style={{ position: "relative", width: "65px" }}>
                              <input
                                type="number"
                                step="0.1"
                                max="100"
                                placeholder="% Alc"
                                value={item.colasAlc || ""}
                                onChange={e => handleItemChange(item.id, 'colasAlc', e.target.value)}
                                style={{ height: "28px", width: "100%", border: "1px solid #CBD5E1", borderRadius: "5px", padding: "2px 16px 2px 6px", fontSize: "11.5px", outline: "none" }}
                              />
                              <span style={{ position: "absolute", right: "5px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#64748B", fontWeight: 700 }}>%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* FILA 3: DESTILADO CON (TOGGLE COMPACTO) */}
                      <div style={{ marginTop: "6px", paddingTop: "4px" }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "11.5px", fontWeight: 700, color: item.esDestiladoCon ? "var(--copper)" : "#64748B" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(item.esDestiladoCon)}
                            onChange={e => handleItemChange(item.id, 'esDestiladoCon', e.target.checked)}
                            style={{ width: "14px", height: "14px", accentColor: "var(--copper)", cursor: "pointer" }}
                          />
                          <span>¿Es "Destilado con"? (ej. Pechuga de pavo, frutas criollas, especias)</span>
                        </label>

                        {item.esDestiladoCon && (
                          <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "6px", background: "rgba(184, 115, 51, 0.04)", border: "1px solid rgba(184, 115, 51, 0.2)", borderRadius: "8px", padding: "8px" }}>
                            {/* Input para agregar ingrediente personalizado */}
                            <div style={{ display: "flex", gap: "6px" }}>
                              <input
                                type="text"
                                id={`ing-input-${item.id}`}
                                placeholder="Escriba ingrediente y presione Enter o Añadir..."
                                style={{ height: "28px", flex: 1, border: "1px solid #CBD5E1", borderRadius: "6px", padding: "2px 8px", fontSize: "11.5px", outline: "none" }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddIngredient(item.id, e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const inputEl = document.getElementById(`ing-input-${item.id}`);
                                  if (inputEl) {
                                    handleAddIngredient(item.id, inputEl.value);
                                    inputEl.value = '';
                                  }
                                }}
                                style={{ background: "var(--copper)", color: "#FFFFFF", border: "none", borderRadius: "6px", padding: "0 12px", fontWeight: 700, fontSize: "11.5px", cursor: "pointer", height: "28px" }}
                              >
                                + Añadir
                              </button>
                            </div>

                            {/* Tags de ingredientes seleccionados */}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                              {(item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : [])).map((ing, ingIdx) => (
                                <span
                                  key={ingIdx}
                                  style={{ background: "#FFFFFF", color: "var(--copper)", border: "1px solid rgba(184, 115, 51, 0.4)", borderRadius: "6px", padding: "2px 6px", fontSize: "11px", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "4px" }}
                                >
                                  🍃 {ing}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveIngredient(item.id, ing)}
                                    style={{ background: "transparent", border: "none", color: "#DC2626", cursor: "pointer", padding: 0, fontWeight: 900, fontSize: "12px" }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>

                            {/* Sugerencias Rápidas */}
                            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", borderTop: "1px dashed rgba(184, 115, 51, 0.2)", paddingTop: "4px" }}>
                              {CATEGORIZED_INGREDIENTS[0].items.concat(CATEGORIZED_INGREDIENTS[1].items.slice(0, 4), CATEGORIZED_INGREDIENTS[2].items.slice(0, 3)).map((sug, sIdx) => {
                                const currentList = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
                                const isAdded = currentList.includes(sug);
                                return (
                                  <button
                                    key={sIdx}
                                    type="button"
                                    onClick={() => isAdded ? handleRemoveIngredient(item.id, sug) : handleAddIngredient(item.id, sug)}
                                    style={{
                                      background: isAdded ? "var(--copper)" : "#FFFFFF",
                                      color: isAdded ? "#FFFFFF" : "#475569",
                                      border: `1px solid ${isAdded ? "var(--copper)" : "#CBD5E1"}`,
                                      borderRadius: "5px",
                                      padding: "1px 6px",
                                      fontSize: "10.5px",
                                      fontWeight: isAdded ? 700 : 500,
                                      cursor: "pointer"
                                    }}
                                  >
                                    {isAdded ? `✓ ${sug}` : `+ ${sug}`}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

                {/* Botón para agregar otro alambique */}
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="btn-add-new"
                    style={{ padding: "6px 14px", fontSize: "12px", background: "#EFF6FF", color: "#1D4ED8", border: "1.5px dashed #BFDBFE" }}
                  >
                    <AddIcon style={{ fontSize: 16 }} />
                    <span>+ Agregar Otro Alambique / Destilación</span>
                  </button>
                </div>



                {/* Observaciones Generales Compactas */}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                  <label style={{ fontSize: "10px", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: "2px" }}>
                    OBSERVACIONES GENERALES
                  </label>
                  <textarea
                    id="destilacionObservaciones"
                    value={formData.observaciones || ""}
                    onChange={e => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                    style={{ width: "100%", minHeight: "44px", maxHeight: "100px", border: "1px solid #CBD5E1", borderRadius: "6px", padding: "6px 8px", fontSize: "12px", outline: "none", resize: "vertical" }}
                    placeholder="Observaciones de segunda destilación, características organolépticas, notas de fuego..."
                  />
                </div>

              </div>

              {/* Botones del Modal */}
              <div className="detail-modal-footer" style={{ marginTop: "14px", padding: "10px 0 0 0", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={e => handleSaveDestilacion(e, "borrador", formSubmissionData)}
                  className="btn-secondary-custom"
                  style={{ background: "#F1F5F9", color: "#334155", fontWeight: 700, padding: "8px 16px", fontSize: "12.5px" }}
                >
                  Guardar Borrador
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  style={{ background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)", color: "#FFFFFF", padding: "8px 20px", fontWeight: 800, fontSize: "12.5px" }}
                >
                  Guardar y Enviar a Revisión UI
                </button>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="btn-secondary-custom"
                  style={{ padding: "8px 16px", fontSize: "12.5px" }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default DestilacionSection;
